import React, { useEffect, useMemo, useState } from 'react';
import {
  FaChevronLeft,
  FaChevronRight,
  FaArrowRight,
  FaBookOpen,
  FaChalkboardTeacher,
  FaEdit,
  FaFolderPlus,
  FaLayerGroup,
  FaListOl,
  FaInfoCircle,
  FaPlus,
  FaProjectDiagram,
  FaSave,
  FaTrash,
  FaUserGraduate,
  FaUsersCog,
  FaClipboardCheck,
} from 'react-icons/fa';
import jsPDF from 'jspdf';
import { utils as XLSXUtils, writeFile as writeXLSXFile } from 'xlsx';
import CourseService from '../services/CourseService';
import api from '../api';
import {
  CONTENT_CREATOR_PERMISSION,
  COURSE_EDITOR_VIEW_PERMISSION,
  COURSE_CLASS_MANAGER_PERMISSION,
  COURSE_COORDINATOR_PERMISSION,
  hasPermission,
  isAdmin,
} from '../utils/permissions';
import { useAuth } from '../AuthContext';
import { resolveAssetUrl } from '../utils/assets';

const emptyLesson = () => ({
  title: '',
  description: '',
  durationMinutes: 30,
  materials: [],
  tasks: [],
  quiz: null,
});
const emptyQuestion = (type = 'text') => ({
  prompt: '',
  type,
  score: 1,
  options: type === 'multiple_choice' ? [''] : [],
  correctOptionIndex: type === 'multiple_choice' ? 0 : null,
  acceptedKeywords: [],
});
const emptyModule = () => ({ title: '', description: '', lessons: [emptyLesson()] });
const createEmptyCourseForm = () => ({
  title: '',
  description: '',
  coverImageUrl: '',
  estimatedHours: 8,
  difficulty: 'iniciante',
  activityNotificationEmail: '',
  prerequisiteCourseIds: [],
  certificateConfig: {
    companyName: '',
    cnpj: '',
    address: '',
    contact: '',
    logoUrl: '',
    signerName: '',
    primaryColor: '#0f766e',
    logoPosition: 'center',
    bodyTextTemplate: 'Certificamos que {{ALUNO}} concluiu com êxito o curso {{CURSO}}, com carga horária de {{CARGA_HORARIA}} horas.',
  },
  requiresAccessGrant: false,
  modules: [emptyModule()],
});

const cloneCourseToForm = (course) => ({
  title: course?.title || '',
  description: course?.description || '',
  coverImageUrl: course?.coverImageUrl || '',
  estimatedHours: Number(course?.estimatedHours || 8),
  difficulty: course?.difficulty || 'iniciante',
  activityNotificationEmail: course?.activityNotificationEmail || '',
  prerequisiteCourseIds: Array.isArray(course?.prerequisiteCourseIds) ? [...course.prerequisiteCourseIds] : [],
  certificateConfig: {
    companyName: course?.certificateConfig?.companyName || '',
    cnpj: course?.certificateConfig?.cnpj || '',
    address: course?.certificateConfig?.address || '',
    contact: course?.certificateConfig?.contact || '',
    logoUrl: course?.certificateConfig?.logoUrl || '',
    signerName: course?.certificateConfig?.signerName || '',
    primaryColor: course?.certificateConfig?.primaryColor || '#0f766e',
    logoPosition: course?.certificateConfig?.logoPosition || 'center',
    bodyTextTemplate:
      course?.certificateConfig?.bodyTextTemplate ||
      'Certificamos que {{ALUNO}} concluiu com êxito o curso {{CURSO}}, com carga horária de {{CARGA_HORARIA}} horas.',
  },
  requiresAccessGrant: Boolean(course?.requiresAccessGrant),
  modules:
    Array.isArray(course?.modules) && course.modules.length > 0
      ? course.modules.map((module) => ({
          id: module.id,
          title: module.title,
          description: module.description || '',
          order: module.order,
          lessons:
            Array.isArray(module.lessons) && module.lessons.length > 0
              ? module.lessons.map((lesson) => ({
                  id: lesson.id,
                  title: lesson.title,
                  description: lesson.description || '',
                  order: lesson.order,
                  durationMinutes: Number(lesson.durationMinutes || 30),
                  materials: Array.isArray(lesson.materials) ? [...lesson.materials] : [],
                  tasks: Array.isArray(lesson.tasks)
                    ? lesson.tasks.map((task) => ({
                        ...task,
                        formQuestions: Array.isArray(task.formQuestions)
                          ? task.formQuestions.map((question) => ({ ...question }))
                          : [],
                      }))
                    : [],
                  quiz: lesson.quiz
                    ? {
                        ...lesson.quiz,
                        questions: Array.isArray(lesson.quiz.questions)
                          ? lesson.quiz.questions.map((question) => ({ ...question }))
                          : [],
                      }
                    : null,
                }))
              : [emptyLesson()],
        }))
      : [emptyModule()],
});

const fileTypeLabel = (mimeType = '') => {
  const normalized = String(mimeType || '').toLowerCase();
  if (normalized.includes('pdf')) return 'pdf';
  if (normalized.includes('video')) return 'video';
  if (normalized.includes('image')) return 'imagem';
  if (normalized.includes('word') || normalized.includes('document')) return 'word';
  return 'arquivo';
};
const isVideoFile = (file) => {
  const mime = String(file?.type || '').toLowerCase();
  const name = String(file?.name || '').toLowerCase();
  return (
    mime.includes('video') ||
    name.endsWith('.mp4') ||
    name.endsWith('.webm') ||
    name.endsWith('.ogg') ||
    name.endsWith('.mov') ||
    name.endsWith('.avi') ||
    name.endsWith('.mkv')
  );
};
const getMaterialSourceUrl = (material = {}) =>
  String(material?.url || material?.downloadUrl || '').trim();
const isYouTubeHost = (value = '') => {
  try {
    const parsed = new URL(String(value || '').trim());
    const hostname = parsed.hostname.replace(/^www\./i, '').toLowerCase();
    return hostname === 'youtu.be' || hostname.endsWith('youtube.com');
  } catch (error) {
    return false;
  }
};
const isVideoMaterial = (material = {}) => {
  const name = String(material?.name || '').toLowerCase();
  const mime = String(material?.mimeType || '').toLowerCase();
  const url = getMaterialSourceUrl(material);
  const normalizedUrl = url.toLowerCase();
  const hasYoutubeVideoId = Boolean(getYouTubeVideoId(url));
  return (
    String(material?.type || '').toLowerCase() === 'video' ||
    mime.includes('video') ||
    name.endsWith('.mp4') ||
    name.endsWith('.webm') ||
    name.endsWith('.ogg') ||
    hasYoutubeVideoId ||
    normalizedUrl.endsWith('.mp4') ||
    normalizedUrl.endsWith('.webm') ||
    normalizedUrl.endsWith('.ogg') ||
    normalizedUrl.endsWith('.mov')
  );
};
const getYouTubeVideoId = (value = '') => {
  const raw = String(value || '').trim();
  if (!raw) return '';
  if (/^[a-zA-Z0-9_-]{11}$/.test(raw)) return raw;
  try {
    const parsed = new URL(raw);
    const hostname = parsed.hostname.replace(/^www\./i, '').toLowerCase();
    if (hostname === 'youtu.be') {
      return parsed.pathname.replace('/', '').split('/')[0] || '';
    }
    if (hostname.endsWith('youtube.com')) {
      if (parsed.pathname === '/watch') {
        return parsed.searchParams.get('v') || '';
      }
      if (parsed.pathname.startsWith('/shorts/')) {
        return parsed.pathname.replace('/shorts/', '').split('/')[0] || '';
      }
      if (parsed.pathname.startsWith('/embed/')) {
        return parsed.pathname.replace('/embed/', '').split('/')[0] || '';
      }
      if (parsed.pathname.startsWith('/live/')) {
        return parsed.pathname.replace('/live/', '').split('/')[0] || '';
      }
    }
  } catch (error) {
    return '';
  }
  return '';
};
const isYoutubeMaterial = (material = {}) =>
  Boolean(
    getYouTubeVideoId(String(material?.url || material?.downloadUrl || '')),
  );
const getYouTubeEmbedUrl = (material = {}) => {
  const videoId = getYouTubeVideoId(String(material?.url || material?.downloadUrl || ''));
  return videoId ? `https://www.youtube.com/embed/${videoId}` : '';
};
const isImageMaterial = (material = {}) => {
  const name = String(material?.name || '').toLowerCase();
  const mime = String(material?.mimeType || '').toLowerCase();
  return mime.includes('image') || /\.(png|jpe?g|gif|webp|svg)$/.test(name);
};
const isPdfMaterial = (material = {}) => {
  const name = String(material?.name || '').toLowerCase();
  const mime = String(material?.mimeType || '').toLowerCase();
  return mime.includes('pdf') || name.endsWith('.pdf');
};

const getFormDurationMinutes = (form) =>
  (form?.modules || []).reduce(
    (acc, module) =>
      acc +
      (module.lessons || []).reduce(
        (lessonAcc, lesson) => lessonAcc + Math.max(1, Number(lesson.durationMinutes || 0)),
        0,
      ),
    0,
  );

const loadImageAsDataURL = async (url) => {
  try {
    const response = await fetch(url);
    if (!response.ok) return null;
    const blob = await response.blob();
    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = () => reject(new Error('Erro ao carregar logo.'));
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
};

const TABS = {
  CATALOG: 'catalog',
  ENROLLED: 'enrolled',
  LEARNING_PATHS: 'learning-paths',
  CREATOR: 'creator',
  COORDINATOR: 'coordinator',
  TEACHER: 'teacher',
  CLASS_DIARY: 'class-diary',
  CERTIFICATE: 'certificate',
};

export default function Courses() {
  const { user } = useAuth();
  const userIsAdmin = isAdmin(user);
  const canCreate = useMemo(
    () => userIsAdmin || hasPermission(user, CONTENT_CREATOR_PERMISSION),
    [user, userIsAdmin],
  );
  const canCoordinate = useMemo(
    () =>
      userIsAdmin ||
      hasPermission(user, COURSE_COORDINATOR_PERMISSION) ||
      hasPermission(user, COURSE_CLASS_MANAGER_PERMISSION),
    [user, userIsAdmin],
  );
  const canViewEditor = useMemo(
    () => userIsAdmin || hasPermission(user, COURSE_EDITOR_VIEW_PERMISSION) || canCoordinate,
    [userIsAdmin, user, canCoordinate],
  );
  const canReviewActivities = useMemo(
    () => (canCoordinate || canCreate || userIsAdmin) && !hasPermission(user, 'ALUNO'),
    [canCoordinate, canCreate, userIsAdmin, user],
  );
  const canEditAttendance = useMemo(
    () => userIsAdmin || hasPermission(user, 'PROFESSOR'),
    [userIsAdmin, user],
  );
  const canAccessClassDiary = useMemo(
    () => canCoordinate || canEditAttendance,
    [canCoordinate, canEditAttendance],
  );

  const [activeTab, setActiveTab] = useState(TABS.ENROLLED);
  const [courses, setCourses] = useState([]);
  const [enrolledCourseIds, setEnrolledCourseIds] = useState([]);
  const [catalogFilter, setCatalogFilter] = useState('all');
  const [selectedCourseId, setSelectedCourseId] = useState(null);
  const [selectedLessonId, setSelectedLessonId] = useState(null);
  const [selectedCourseDetailTab, setSelectedCourseDetailTab] = useState('trilha');
  const [isLearningOverlayOpen, setIsLearningOverlayOpen] = useState(false);
  const [editingCourseId, setEditingCourseId] = useState(null);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [classrooms, setClassrooms] = useState([]);
  const [users, setUsers] = useState([]);
  const [studentUsers, setStudentUsers] = useState([]);
  const [studentSearchTerm, setStudentSearchTerm] = useState('');
  const [classroomForm, setClassroomForm] = useState({
    name: '',
    description: '',
    status: 'ACTIVE',
    coordinatorUserId: user?.id || null,
    studentIds: [],
    courseIds: [],
  });
  const [editingClassroomId, setEditingClassroomId] = useState(null);
  const [grantForm, setGrantForm] = useState({
    courseId: null,
    userId: null,
    accessStartAt: '',
    accessEndAt: '',
  });
  const [form, setForm] = useState(createEmptyCourseForm);
  const [progressByCourse, setProgressByCourse] = useState({});
  const [taskAnswers, setTaskAnswers] = useState({});
  const [quizAnswers, setQuizAnswers] = useState({});
  const [taskAttachments, setTaskAttachments] = useState({});
  const [instructorSubmissions, setInstructorSubmissions] = useState([]);
  const [attendanceClassrooms, setAttendanceClassrooms] = useState([]);
  const [classDiaries, setClassDiaries] = useState([]);
  const [selectedDiaryId, setSelectedDiaryId] = useState(null);
  const [editingDiaryId, setEditingDiaryId] = useState(null);
  const [isRegisteringFrequency, setIsRegisteringFrequency] = useState(false);
  const [attendanceClassroomId, setAttendanceClassroomId] = useState(null);
  const [attendanceDate, setAttendanceDate] = useState(new Date().toISOString().slice(0, 10));
  const [diaryTheme, setDiaryTheme] = useState('');
  const [diaryObjective, setDiaryObjective] = useState('');
  const [attendanceRecords, setAttendanceRecords] = useState([]);
  const [attendanceRows, setAttendanceRows] = useState([]);
  const [selectedAttendanceStudentId, setSelectedAttendanceStudentId] = useState(null);
  const [attendanceSaving, setAttendanceSaving] = useState(false);
  const [attendanceExporting, setAttendanceExporting] = useState(false);
  const [attendanceFilters, setAttendanceFilters] = useState({
    classroomId: 'all',
    studentId: 'all',
    teacherId: 'all',
  });
  const [certificateQuery, setCertificateQuery] = useState('');
  const [certificateResult, setCertificateResult] = useState(null);
  const [instructorFilters, setInstructorFilters] = useState({
    studentId: 'all',
    courseId: 'all',
    classroom: 'all',
  });
  const [learningPaths, setLearningPaths] = useState([]);
  const [selectedLearningPathId, setSelectedLearningPathId] = useState(null);
  const [learningPathForm, setLearningPathForm] = useState({
    name: '',
    description: '',
    audience: '',
    courseIds: [],
  });
  const [editingLearningPathId, setEditingLearningPathId] = useState(null);
  const [materialUploadStatus, setMaterialUploadStatus] = useState({
    visible: false,
    progress: 0,
    fileName: '',
  });

  const selectedCourse = courses.find((course) => course.id === selectedCourseId) || null;
  const selectedCourseProgress = selectedCourseId ? progressByCourse[selectedCourseId] : null;
  const unlockedLessonIds = useMemo(() => {
    if (!selectedCourse) return new Set();
    const orderedLessonIds = (selectedCourse.modules || [])
      .flatMap((module) => module.lessons || [])
      .map((lesson) => lesson.id);
    const completedIds = new Set(selectedCourseProgress?.enrollment?.completedLessonIds || []);
    const unlocked = new Set();
    let canUnlockNext = true;
    orderedLessonIds.forEach((lessonId) => {
      if (canUnlockNext) unlocked.add(lessonId);
      if (!completedIds.has(lessonId)) canUnlockNext = false;
    });
    return unlocked;
  }, [selectedCourse, selectedCourseProgress]);
  const orderedLessons = useMemo(
    () =>
      (selectedCourse?.modules || []).flatMap((module) =>
        (module.lessons || []).map((lesson) => ({ ...lesson, moduleTitle: module.title, moduleOrder: module.order })),
      ),
    [selectedCourse],
  );
  const currentLesson = useMemo(
    () => orderedLessons.find((lesson) => lesson.id === selectedLessonId) || orderedLessons[0] || null,
    [orderedLessons, selectedLessonId],
  );
  const currentLessonIndex = useMemo(
    () => orderedLessons.findIndex((lesson) => lesson.id === currentLesson?.id),
    [orderedLessons, currentLesson],
  );
  const previousLesson = currentLessonIndex > 0 ? orderedLessons[currentLessonIndex - 1] : null;
  const nextLesson =
    currentLessonIndex >= 0 && currentLessonIndex < orderedLessons.length - 1
      ? orderedLessons[currentLessonIndex + 1]
      : null;
  const creatorCourses = useMemo(() => {
    if (!canCreate) return [];
    return courses.filter((course) => userIsAdmin || Number(course.creatorId) === Number(user?.id));
  }, [courses, canCreate, userIsAdmin, user?.id]);
  const filteredCatalogCourses = useMemo(() => {
    if (catalogFilter === 'enrolled') {
      return courses.filter((course) => enrolledCourseIds.includes(course.id));
    }
    if (catalogFilter === 'not-enrolled') {
      return courses.filter((course) => !enrolledCourseIds.includes(course.id));
    }
    return courses;
  }, [courses, enrolledCourseIds, catalogFilter]);
  const enrolledCourses = useMemo(
    () => courses.filter((course) => enrolledCourseIds.includes(course.id)),
    [courses, enrolledCourseIds],
  );
  const filteredStudentUsers = useMemo(() => {
    const normalizedTerm = studentSearchTerm.trim().toLowerCase();
    if (!normalizedTerm) return studentUsers;
    return studentUsers.filter((entry) => {
      const name = String(entry?.name || '').toLowerCase();
      const email = String(entry?.email || '').toLowerCase();
      return name.includes(normalizedTerm) || email.includes(normalizedTerm);
    });
  }, [studentUsers, studentSearchTerm]);
  const selectedAttendanceClassroom = useMemo(
    () => attendanceClassrooms.find((entry) => Number(entry.id) === Number(attendanceClassroomId)) || null,
    [attendanceClassrooms, attendanceClassroomId],
  );
  const selectedDiary = useMemo(
    () => classDiaries.find((entry) => Number(entry.id) === Number(selectedDiaryId)) || null,
    [classDiaries, selectedDiaryId],
  );
  const filteredAttendanceRecords = useMemo(
    () =>
      attendanceRecords.filter((entry) => {
        if (attendanceFilters.classroomId !== 'all' && Number(attendanceFilters.classroomId) !== Number(entry.classroomId)) return false;
        if (attendanceFilters.studentId !== 'all' && Number(attendanceFilters.studentId) !== Number(entry.studentId)) return false;
        if (attendanceFilters.teacherId !== 'all' && Number(attendanceFilters.teacherId) !== Number(entry.teacherId)) return false;
        return true;
      }),
    [attendanceRecords, attendanceFilters],
  );
  const selectedAttendanceStudent = useMemo(
    () => studentUsers.find((entry) => Number(entry.id) === Number(selectedAttendanceStudentId)) || null,
    [studentUsers, selectedAttendanceStudentId],
  );
  const selectedAttendanceRow = useMemo(
    () => attendanceRows.find((entry) => Number(entry.studentId) === Number(selectedAttendanceStudentId)) || null,
    [attendanceRows, selectedAttendanceStudentId],
  );

  useEffect(() => {
    if (activeTab !== TABS.ENROLLED) {
      setIsLearningOverlayOpen(false);
    }
  }, [activeTab]);
  const filteredInstructorSubmissions = useMemo(
    () =>
      instructorSubmissions.filter((entry) => {
        const byStudent =
          instructorFilters.studentId === 'all' || Number(instructorFilters.studentId) === Number(entry.studentId);
        const byCourse =
          instructorFilters.courseId === 'all' || Number(instructorFilters.courseId) === Number(entry.courseId);
        const byClassroom =
          instructorFilters.classroom === 'all' || (entry.classroomNames || []).includes(instructorFilters.classroom);
        return byStudent && byCourse && byClassroom;
      }),
    [instructorSubmissions, instructorFilters],
  );
  const canManageLearningPaths = userIsAdmin || hasPermission(user, COURSE_EDITOR_VIEW_PERMISSION);
  const selectedLearningPath = useMemo(
    () => learningPaths.find((path) => path.id === selectedLearningPathId) || learningPaths[0] || null,
    [learningPaths, selectedLearningPathId],
  );
  const dashboardHighlights = useMemo(
    () => [
      {
        label: 'Cursos no catálogo',
        value: courses.length,
      },
      {
        label: 'Matrículas ativas',
        value: enrolledCourseIds.length,
      },
      {
        label: 'Turmas gerenciadas',
        value: classrooms.length,
      },
      {
        label: 'Atividades pendentes',
        value: instructorSubmissions.length,
      },
    ],
    [courses.length, enrolledCourseIds.length, classrooms.length, instructorSubmissions.length],
  );

  const loadCourses = async () => {
    setLoading(true);
    try {
      const data = await CourseService.list();
      setCourses(data);
      const enrollments = await CourseService.listMyEnrollments();
      setEnrolledCourseIds(Array.isArray(enrollments) ? enrollments : []);
      if (!selectedCourseId && data.length > 0) {
        setSelectedCourseId(data[0].id);
      }
    } catch (error) {
      setMessage(error?.response?.data?.error || 'Falha ao carregar cursos.');
    } finally {
      setLoading(false);
    }
  };

  const loadCourseProgress = async (courseId) => {
    if (!courseId) return;
    try {
      const response = await CourseService.progress(courseId);
      setProgressByCourse((prev) => ({
        ...prev,
        [courseId]: response,
      }));
    } catch {
      // silêncio: progresso pode falhar para não matriculados
    }
  };

  const loadCoordinatorData = async () => {
    if (!canCoordinate) return;
    try {
      const [usersResponse, classroomsResponse] = await Promise.all([
        api.get('/courses/admin/users'),
        api.get('/courses/classrooms'),
      ]);
      setUsers(usersResponse.data?.allUsers || usersResponse.data?.data || []);
      setStudentUsers(usersResponse.data?.data || []);
      setClassroomForm((prev) => ({
        ...prev,
        studentIds: prev.studentIds.filter((id) => (usersResponse.data?.data || []).some((entry) => entry.id === id)),
      }));
      setGrantForm((prev) => ({
        ...prev,
        userId: (usersResponse.data?.data || []).some((entry) => entry.id === prev.userId) ? prev.userId : null,
      }));
      setClassrooms(classroomsResponse.data?.data || []);
    } catch (error) {
      setMessage(error?.response?.data?.error || 'Falha ao carregar dados de coordenação.');
    }
  };

  const loadAttendanceData = async () => {
    if (!canAccessClassDiary) return;
    try {
      const [classroomsData, recordsData, usersResponse, diariesData] = await Promise.all([
        CourseService.listAttendanceClassrooms(),
        CourseService.listAttendance(selectedDiaryId ? { diaryId: selectedDiaryId } : {}),
        api.get('/courses/admin/users'),
        CourseService.listClassDiaries(),
      ]);
      const activeClassrooms = Array.isArray(classroomsData) ? classroomsData : [];
      setAttendanceClassrooms(activeClassrooms);
      setUsers(usersResponse.data?.allUsers || usersResponse.data?.data || []);
      setStudentUsers(usersResponse.data?.data || []);
      setClassDiaries(Array.isArray(diariesData) ? diariesData : []);
      if (!attendanceClassroomId && activeClassrooms.length > 0) {
        setAttendanceClassroomId(activeClassrooms[0].id);
      }
      if (!selectedDiaryId && Array.isArray(diariesData) && diariesData.length > 0) {
        setSelectedDiaryId(diariesData[0].id);
      }
      setAttendanceRecords(Array.isArray(recordsData) ? recordsData : []);
    } catch (error) {
      setMessage(error?.response?.data?.error || 'Falha ao carregar diário de classe.');
    }
  };

  const saveAttendance = async () => {
    if (!selectedDiaryId) {
      setMessage('Selecione ou crie um diário de classe antes de registrar frequência.');
      return;
    }
    if (!attendanceClassroomId || !canEditAttendance) {
      setMessage('Somente usuários com permissão de professor podem registrar frequência.');
      return;
    }
    if (attendanceRows.length === 0) {
      setMessage('Selecione uma turma com alunos para registrar frequência.');
      return;
    }
    setAttendanceSaving(true);
    try {
      await CourseService.recordAttendance(attendanceClassroomId, {
        diaryId: selectedDiaryId,
        attendanceDate,
        records: attendanceRows.map((row) => ({
          studentId: row.studentId,
          status: row.status || 'PRESENTE',
          lessonTopic: row.lessonTopic || diaryTheme || '',
          objective: row.objective || diaryObjective || '',
          allDay: row.allDay !== false,
          delayHours: row.allDay ? 0 : Number(row.delayHours || 0),
          delayMinutes: row.allDay ? 0 : Number(row.delayMinutes || 0),
          studentObservation: row.studentObservation || '',
          psychologistObservation: Boolean(row.psychologistObservation),
          pcd: Boolean(row.pcd),
        })),
      });
      setMessage('Diário de classe salvo com sucesso.');
      await loadAttendanceData();
    } catch (error) {
      setMessage(error?.response?.data?.error || 'Não foi possível salvar o diário de classe.');
    } finally {
      setAttendanceSaving(false);
    }
  };

  const saveClassDiary = async () => {
    if (!attendanceClassroomId) {
      setMessage('Selecione a turma para criar o diário de classe.');
      return;
    }
    try {
      if (editingDiaryId) {
        await CourseService.updateClassDiary(editingDiaryId, {
          diaryDate: attendanceDate,
          theme: diaryTheme,
          objective: diaryObjective,
        });
        setMessage('Diário de classe atualizado com sucesso.');
      } else {
        const created = await CourseService.createClassDiary({
          classroomId: attendanceClassroomId,
          diaryDate: attendanceDate,
          theme: diaryTheme,
          objective: diaryObjective,
        });
        if (created?.id) setSelectedDiaryId(created.id);
        setMessage('Diário de classe criado com sucesso.');
      }
      setEditingDiaryId(null);
      await loadAttendanceData();
    } catch (error) {
      setMessage(error?.response?.data?.error || 'Não foi possível salvar o diário de classe.');
    }
  };

  const startEditDiary = (diary) => {
    setEditingDiaryId(diary.id);
    setSelectedDiaryId(diary.id);
    setDiaryTheme(diary.theme || '');
    setDiaryObjective(diary.objective || '');
    setAttendanceDate(diary.diaryDate || new Date().toISOString().slice(0, 10));
    setAttendanceClassroomId(diary.classroomId || null);
  };

  const removeDiary = async (diaryId) => {
    try {
      await CourseService.deleteClassDiary(diaryId);
      if (selectedDiaryId === diaryId) {
        setSelectedDiaryId(null);
        setIsRegisteringFrequency(false);
      }
      setMessage('Diário de classe excluído com sucesso.');
      await loadAttendanceData();
    } catch (error) {
      setMessage(error?.response?.data?.error || 'Não foi possível excluir o diário de classe.');
    }
  };

  const exportAttendanceXls = () => {
    if (!filteredAttendanceRecords.length) {
      setMessage('Não há frequência para exportar com os filtros selecionados.');
      return;
    }
    setAttendanceExporting(true);
    try {
      const rows = filteredAttendanceRecords.map((entry) => ({
        Data: new Date(`${entry.attendanceDate}T00:00:00Z`).toLocaleDateString('pt-BR'),
        Turma: attendanceClassrooms.find((classroom) => classroom.id === entry.classroomId)?.name || `Turma #${entry.classroomId}`,
        Aluno: studentUsers.find((student) => student.id === entry.studentId)?.name || `Aluno #${entry.studentId}`,
        Professor: users.find((teacher) => teacher.id === entry.teacherId)?.name || `Professor #${entry.teacherId}`,
        Situação: entry.status,
      }));
      const worksheet = XLSXUtils.json_to_sheet(rows);
      const workbook = XLSXUtils.book_new();
      XLSXUtils.book_append_sheet(workbook, worksheet, 'Frequencia');
      writeXLSXFile(workbook, `frequencia-diario-classe-${new Date().toISOString().slice(0, 10)}.xlsx`, { bookType: 'xlsx' });
    } catch (error) {
      setMessage('Não foi possível gerar o arquivo XLSX.');
    } finally {
      setAttendanceExporting(false);
    }
  };

  const exportAttendancePdf = async () => {
    if (!filteredAttendanceRecords.length) {
      setMessage('Não há frequência para exportar com os filtros selecionados.');
      return;
    }
    setAttendanceExporting(true);
    try {
      const doc = new jsPDF({ orientation: 'landscape' });
      const pageWidth = doc.internal.pageSize.getWidth();
      const margin = 12;
      let cursorY = 16;
      doc.setFillColor(2, 4, 59);
      doc.rect(0, 0, pageWidth, 34, 'F');
      const logoPath = `${process.env.PUBLIC_URL || ''}/nashlogoazul.png`;
      const logoDataUrl = await loadImageAsDataURL(logoPath);
      if (logoDataUrl) {
        doc.addImage(logoDataUrl, 'PNG', margin, 4, 28, 28);
      }
      doc.setTextColor(255, 255, 255);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(15);
      doc.text('Nash Tecnologia • Diário de Classe', 44, 16);
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.text(`Relatório de frequência emitido em ${new Date().toLocaleString('pt-BR')}`, 44, 24);
      doc.text('Intelligent BPM Nash Tecnologia • www.nashtec.com.br', 44, 30);
      doc.setTextColor(31, 41, 55);
      cursorY = 44;
      doc.setFontSize(9);
      const headers = ['Data', 'Turma', 'Aluno', 'Professor', 'Situação'];
      const rows = filteredAttendanceRecords.map((entry) => [
        new Date(`${entry.attendanceDate}T00:00:00Z`).toLocaleDateString('pt-BR'),
        attendanceClassrooms.find((classroom) => classroom.id === entry.classroomId)?.name || `Turma #${entry.classroomId}`,
        studentUsers.find((student) => student.id === entry.studentId)?.name || `Aluno #${entry.studentId}`,
        users.find((teacher) => teacher.id === entry.teacherId)?.name || `Professor #${entry.teacherId}`,
        entry.status,
      ]);
      const colWidths = [30, 65, 70, 70, 40];
      headers.forEach((header, index) => {
        const x = margin + colWidths.slice(0, index).reduce((acc, value) => acc + value, 0);
        doc.setFont('helvetica', 'bold');
        doc.text(header, x, cursorY);
      });
      cursorY += 6;
      doc.setFont('helvetica', 'normal');
      rows.forEach((row) => {
        if (cursorY > 190) {
          doc.addPage();
          cursorY = 16;
        }
        row.forEach((value, index) => {
          const x = margin + colWidths.slice(0, index).reduce((acc, width) => acc + width, 0);
          doc.text(String(value), x, cursorY);
        });
        cursorY += 6;
      });
      doc.save(`frequencia-diario-classe-${new Date().toISOString().slice(0, 10)}.pdf`);
    } catch (error) {
      setMessage('Não foi possível gerar o relatório em PDF.');
    } finally {
      setAttendanceExporting(false);
    }
  };

  useEffect(() => {
    loadCourses();
  }, []);

  useEffect(() => {
    try {
      const raw = localStorage.getItem('courses.learningPaths.v1');
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        setLearningPaths(parsed);
        if (parsed[0]?.id) setSelectedLearningPathId(parsed[0].id);
      }
    } catch {
      // silêncio: sem trilhas salvas localmente
    }
  }, []);

  useEffect(() => {
    if (!learningPaths.length) {
      setSelectedLearningPathId(null);
      return;
    }
    if (!learningPaths.some((path) => path.id === selectedLearningPathId)) {
      setSelectedLearningPathId(learningPaths[0].id);
    }
  }, [learningPaths, selectedLearningPathId]);

  useEffect(() => {
    localStorage.setItem('courses.learningPaths.v1', JSON.stringify(learningPaths));
  }, [learningPaths]);

  useEffect(() => {
    loadCoordinatorData();
  }, [canCoordinate]);

  useEffect(() => {
    loadAttendanceData();
  }, [canAccessClassDiary]);

  useEffect(() => {
    const loadDiaryAttendance = async () => {
      if (!selectedDiaryId) {
        setAttendanceRecords([]);
        return;
      }
      try {
        const recordsData = await CourseService.listAttendance({ diaryId: selectedDiaryId });
        setAttendanceRecords(Array.isArray(recordsData) ? recordsData : []);
      } catch (error) {
        setMessage(error?.response?.data?.error || 'Não foi possível carregar frequências do diário.');
      }
    };
    loadDiaryAttendance();
  }, [selectedDiaryId]);

  useEffect(() => {
    if (!selectedDiary) return;
    setAttendanceClassroomId(selectedDiary.classroomId);
    setAttendanceDate(selectedDiary.diaryDate);
    setDiaryTheme(selectedDiary.theme || '');
    setDiaryObjective(selectedDiary.objective || '');
  }, [selectedDiary]);

  useEffect(() => {
    if (!selectedCourseId) return;
    loadCourseProgress(selectedCourseId);
  }, [selectedCourseId]);

  useEffect(() => {
    if (!orderedLessons.length) {
      setSelectedLessonId(null);
      return;
    }
    const firstUnlocked = orderedLessons.find((lesson) => unlockedLessonIds.has(lesson.id)) || orderedLessons[0];
    if (!selectedLessonId || !orderedLessons.some((lesson) => lesson.id === selectedLessonId)) {
      setSelectedLessonId(firstUnlocked.id);
    }
  }, [orderedLessons, unlockedLessonIds, selectedLessonId]);

  useEffect(() => {
    setActiveTab(TABS.ENROLLED);
  }, []);

  useEffect(() => {
    const loadInstructorSubmissions = async () => {
      if (!canReviewActivities) return;
      try {
        const data = await CourseService.listInstructorSubmissions();
        setInstructorSubmissions(Array.isArray(data) ? data : []);
      } catch (error) {
        setMessage(error?.response?.data?.error || 'Não foi possível carregar atividades recebidas.');
      }
    };
    loadInstructorSubmissions();
  }, [canReviewActivities]);

  useEffect(() => {
    if (!selectedAttendanceClassroom) {
      setAttendanceRows([]);
      setSelectedAttendanceStudentId(null);
      return;
    }
    const rows = (selectedAttendanceClassroom.studentIds || []).map((studentId) => {
      const existing = attendanceRecords.find(
        (entry) =>
          Number(entry.classroomId) === Number(selectedAttendanceClassroom.id) &&
          Number(entry.studentId) === Number(studentId) &&
          entry.attendanceDate === attendanceDate,
      );
      return {
        studentId,
        status: existing?.status || 'PRESENTE',
        lessonTopic: existing?.lessonTopic || selectedDiary?.theme || '',
        objective: existing?.objective || selectedDiary?.objective || '',
        allDay: existing?.allDay === undefined ? true : Boolean(existing?.allDay),
        delayHours: Number(existing?.delayHours || 0),
        delayMinutes: Number(existing?.delayMinutes || 0),
        studentObservation: existing?.studentObservation || '',
        psychologistObservation: Boolean(existing?.psychologistObservation),
        pcd: Boolean(existing?.pcd),
      };
    });
    setAttendanceRows(rows);
  }, [selectedAttendanceClassroom, attendanceRecords, attendanceDate, selectedDiary]);

  useEffect(() => {
    if (!attendanceRows.length) {
      setSelectedAttendanceStudentId(null);
      return;
    }
    if (!attendanceRows.some((entry) => Number(entry.studentId) === Number(selectedAttendanceStudentId))) {
      setSelectedAttendanceStudentId(attendanceRows[0].studentId);
    }
  }, [attendanceRows, selectedAttendanceStudentId]);

  const setAttendanceStatusForStudent = (studentId, status) => {
    setAttendanceRows((prev) =>
      prev.map((entry) =>
        Number(entry.studentId) === Number(studentId)
          ? { ...entry, status }
          : entry,
      ),
    );
  };
  const updateAttendanceFieldForStudent = (studentId, field, value) => {
    setAttendanceRows((prev) =>
      prev.map((entry) =>
        Number(entry.studentId) === Number(studentId)
          ? { ...entry, [field]: value }
          : entry,
      ),
    );
  };

  const updateModule = (index, nextModule) => {
    setForm((prev) => {
      const modules = [...prev.modules];
      modules[index] = nextModule;
      return { ...prev, modules };
    });
  };

  const handleUploadMaterial = async (moduleIndex, lessonIndex, file) => {
    const body = new FormData();
    body.append('file', file);
    setMaterialUploadStatus({
      visible: true,
      progress: 0,
      fileName: file.name || 'arquivo',
    });
    let uploadedSuccessfully = false;
    try {
      const response = await api.post('/upload', body, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          if (!progressEvent?.total) return;
          const computedProgress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setMaterialUploadStatus((prev) => ({
            ...prev,
            progress: Math.max(prev.progress, computedProgress),
          }));
        },
      });

      const material = {
        name: response.data?.name || file.name,
        type: fileTypeLabel(file.type),
        url: response.data?.url,
        downloadUrl: response.data?.downloadUrl,
        relativePath: response.data?.relativePath,
        mimeType: file.type,
        size: file.size,
      };

      setForm((prev) => {
        const modules = [...prev.modules];
        const lesson = { ...modules[moduleIndex].lessons[lessonIndex] };
        lesson.materials = [...lesson.materials, material];
        modules[moduleIndex] = {
          ...modules[moduleIndex],
          lessons: modules[moduleIndex].lessons.map((item, idx) =>
            idx === lessonIndex ? lesson : item,
          ),
        };
        return { ...prev, modules };
      });
      uploadedSuccessfully = true;
    } catch (error) {
      setMessage(error?.response?.data?.error || 'Não foi possível enviar o material da aula.');
    } finally {
      if (uploadedSuccessfully) {
        setMaterialUploadStatus((prev) => ({ ...prev, progress: 100 }));
        setTimeout(() => {
          setMaterialUploadStatus({
            visible: false,
            progress: 0,
            fileName: '',
          });
        }, 250);
      } else {
        setMaterialUploadStatus({
          visible: false,
          progress: 0,
          fileName: '',
        });
      }
    }
  };

  const handleAddVideoLinkMaterial = (moduleIndex, lessonIndex) => {
    const link = window.prompt('Cole o link do vídeo hospedado externamente para esta aula:');
    if (!link) return;
    const normalizedLink = link.trim();
    if (!/^https?:\/\//i.test(normalizedLink)) {
      setMessage('Link inválido. Informe uma URL completa iniciando com http:// ou https://.');
      return;
    }
    const titleInput = window.prompt('Nome do vídeo (opcional):');
    const videoId = getYouTubeVideoId(normalizedLink);
    if (isYouTubeHost(normalizedLink) && !videoId) {
      setMessage('Link do YouTube inválido para aula. Cole o link direto do vídeo (ex.: https://www.youtube.com/watch?v=ID).');
      return;
    }
    const material = {
      name: String(titleInput || '').trim() || 'Vídeo por link',
      type: 'video',
      url: normalizedLink,
      downloadUrl: normalizedLink,
      relativePath: null,
      mimeType: videoId ? 'video/youtube' : 'video/external-link',
      size: null,
      addedAt: new Date().toISOString(),
    };
    setForm((prev) => {
      const modules = [...prev.modules];
      const lesson = { ...modules[moduleIndex].lessons[lessonIndex] };
      lesson.materials = [...(lesson.materials || []), material];
      modules[moduleIndex] = {
        ...modules[moduleIndex],
        lessons: modules[moduleIndex].lessons.map((item, idx) =>
          idx === lessonIndex ? lesson : item,
        ),
      };
      return { ...prev, modules };
    });
  };

  const resetCreatorForm = () => {
    setEditingCourseId(null);
    setForm(createEmptyCourseForm());
  };

  const startEditCourse = (course) => {
    setEditingCourseId(course.id);
    setForm(cloneCourseToForm(course));
    setActiveTab(TABS.CREATOR);
  };

  const saveCourse = async () => {
    if (!canCreate) {
      setMessage('Você possui apenas visualização da edição de cursos.');
      return;
    }
    setSaving(true);
    setMessage('');
    try {
      const computedDurationMinutes = getFormDurationMinutes(form);
      const payload = {
        ...form,
        estimatedHours: Math.max(1, Math.round(computedDurationMinutes / 60)),
      };
      if (!String(form?.certificateConfig?.signerName || '').trim()) {
        setMessage('Informe o nome de quem assina o certificado na edição do curso.');
        setSaving(false);
        return;
      }
      if (editingCourseId) {
        await CourseService.update(editingCourseId, payload);
        setMessage('Curso atualizado com sucesso.');
      } else {
        await CourseService.create(payload);
        setMessage('Curso criado com sucesso.');
      }
      resetCreatorForm();
      await loadCourses();
    } catch (error) {
      setMessage(error?.response?.data?.error || 'Não foi possível salvar o curso.');
    } finally {
      setSaving(false);
    }
  };

  const publishCourse = async (courseId) => {
    try {
      await CourseService.publish(courseId);
      setMessage('Curso publicado com sucesso.');
      await loadCourses();
    } catch (error) {
      setMessage(error?.response?.data?.error || 'Não foi possível publicar o curso.');
    }
  };

  const enrollCourse = async (courseId) => {
    try {
      await CourseService.enroll(courseId);
      setMessage('Matrícula realizada com sucesso.');
      const enrollments = await CourseService.listMyEnrollments();
      setEnrolledCourseIds(Array.isArray(enrollments) ? enrollments : []);
      setSelectedCourseId(courseId);
    } catch (error) {
      setMessage(error?.response?.data?.error || 'Não foi possível realizar a matrícula.');
    }
  };

  const unenrollCourse = async (courseId) => {
    try {
      await CourseService.unenroll(courseId);
      setMessage('Desmatrícula realizada com sucesso.');
      const enrollments = await CourseService.listMyEnrollments();
      setEnrolledCourseIds(Array.isArray(enrollments) ? enrollments : []);
      if (selectedCourseId === courseId) {
        setSelectedCourseId(null);
      }
    } catch (error) {
      setMessage(error?.response?.data?.error || 'Não foi possível desmatricular.');
    }
  };

  const generateCertificate = async (course) => {
    const issued = await CourseService.issueCertificate(course.id);
    const cfg = course?.certificateConfig || {};
    const companyName = cfg.companyName || '';
    const cnpj = cfg.cnpj || '';
    const address = cfg.address || '';
    const contact = cfg.contact || '';
    const signerName = cfg.signerName || 'Assinatura não informada';
    const logoUrl = resolveAssetUrl(cfg.logoUrl || '');
    const logoPosition = cfg.logoPosition || 'center';
    const logoAlign = logoPosition === 'left' ? 'left' : logoPosition === 'right' ? 'right' : 'center';
    const bodyText = String(cfg.bodyTextTemplate || '')
      .replaceAll('{{ALUNO}}', user?.name || 'Aluno')
      .replaceAll('{{CURSO}}', course?.title || '')
      .replaceAll('{{CARGA_HORARIA}}', String(course?.estimatedHours || 0));
    const content = `
      <html><head><meta charset="utf-8" /><title>Certificado</title></head>
      <body style="font-family:Arial;padding:40px;text-align:center;border:12px solid ${cfg.primaryColor || '#d4af37'}">
      <h1 style="font-size:52px;margin:0 0 10px 0">Certificado</h1>
      <h2 style="margin:0 0 20px 0">${companyName}</h2>
      <h2>Certificado de Conclusão</h2>
      <p>${bodyText}</p>
      <p>Emitido em ${new Date().toLocaleDateString('pt-BR')}.</p>
      <div style="margin:30px auto 10px auto;width:320px;border-top:1px solid #333;padding-top:8px">
        <strong>${signerName}</strong><br/><span>Responsável pela assinatura</span>
      </div>
      ${logoUrl ? `<div style="text-align:${logoAlign}"><img src="${logoUrl}" alt="Logo" style="max-height:70px;margin-top:16px"/></div>` : ''}
      <p style="margin-top:10px;font-size:12px">ID de referência do certificado: <strong>${issued.referenceId}</strong></p>
      <p style="margin-top:4px;font-size:12px">Consulta pública: <strong>${window.location.origin}/certificados/consulta</strong></p>
      <p style="margin-top:30px;font-size:13px">${companyName}${cnpj ? ` - CNPJ ${cnpj}` : ''}${address ? ` - ${address}` : ''}${contact ? ` - Contato: ${contact}` : ''}</p>
      </body></html>`;
    const blob = new Blob([content], { type: 'text/html;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `certificado-${(course?.title || 'curso').replace(/\s+/g, '-').toLowerCase()}.html`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const markLessonAsDone = async (courseId, lessonId) => {
    try {
      await CourseService.enroll(courseId);
      await CourseService.completeLesson(courseId, lessonId);
      setMessage('Aula concluída com sucesso.');
      await loadCourseProgress(courseId);
      const enrollments = await CourseService.listMyEnrollments();
      setEnrolledCourseIds(Array.isArray(enrollments) ? enrollments : []);
    } catch (error) {
      setMessage(error?.response?.data?.error || 'Não foi possível concluir a aula.');
    }
  };

  const markLessonAsNotDone = async (courseId, lessonId) => {
    try {
      await CourseService.uncompleteLesson(courseId, lessonId);
      setMessage('Aula marcada como não concluída.');
      await loadCourseProgress(courseId);
    } catch (error) {
      setMessage(error?.response?.data?.error || 'Não foi possível voltar a aula para não concluída.');
    }
  };

  const uploadCourseCover = async (file) => {
    const body = new FormData();
    body.append('file', file);
    const response = await api.post('/upload', body, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    setForm((prev) => ({ ...prev, coverImageUrl: response.data?.url || '' }));
  };

  const uploadCertificateLogo = async (file) => {
    const body = new FormData();
    body.append('file', file);
    const response = await api.post('/upload', body, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    setForm((prev) => ({
      ...prev,
      certificateConfig: {
        ...(prev.certificateConfig || {}),
        logoUrl: response.data?.url || '',
      },
    }));
  };

  const uploadAnswerAttachment = async (file) => {
    const body = new FormData();
    body.append('file', file);
    const response = await api.post('/upload', body, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data?.url || '';
  };

  const getCourseDurationMinutes = (course) =>
    (course?.modules || []).reduce(
      (acc, module) =>
        acc +
        (module.lessons || []).reduce(
          (lessonAcc, lesson) => lessonAcc + Math.max(1, Number(lesson.durationMinutes || 0)),
          0,
        ),
      0,
    );

  const updateTaskAnswer = (taskId, questionIndex, value) => {
    setTaskAnswers((prev) => {
      const current = prev[taskId] || [];
      const next = [...current];
      next[questionIndex] = value;
      return { ...prev, [taskId]: next };
    });
  };

  const updateQuizAnswer = (lessonId, questionIndex, value) => {
    setQuizAnswers((prev) => {
      const current = prev[lessonId] || [];
      const next = [...current];
      next[questionIndex] = value;
      return { ...prev, [lessonId]: next };
    });
  };

  const submitTaskForm = async (courseId, lessonId, task) => {
    try {
      const answers = taskAnswers[task.id] || [];
      const result = await CourseService.submitTask(courseId, lessonId, task.id, {
        answers,
        attachmentUrl: taskAttachments[task.id] || null,
      });
      setMessage(
        typeof result?.score === 'number'
          ? `Tarefa enviada. Nota automática: ${result.score}`
          : 'Tarefa enviada com sucesso.',
      );
      setTaskAttachments((prev) => ({ ...prev, [task.id]: null }));
      await loadCourseProgress(courseId);
    } catch (error) {
      setMessage(error?.response?.data?.error || 'Não foi possível enviar a tarefa.');
    }
  };

  const submitQuizForm = async (courseId, lessonId) => {
    try {
      const answers = quizAnswers[lessonId] || [];
      const result = await CourseService.submitQuiz(courseId, lessonId, answers);
      setMessage(`Avaliação enviada. Nota: ${result?.score ?? 0}`);
      await loadCourseProgress(courseId);
    } catch (error) {
      setMessage(error?.response?.data?.error || 'Não foi possível enviar a avaliação.');
    }
  };

  const toggleMultiSelect = (values, value) => {
    if (values.includes(value)) {
      return values.filter((entry) => entry !== value);
    }
    return [...values, value];
  };

  const createClassroom = async () => {
    try {
      if (editingClassroomId) {
        await api.put(`/courses/classrooms/${editingClassroomId}`, classroomForm);
        setMessage('Turma atualizada com sucesso.');
      } else {
        await api.post('/courses/classrooms', classroomForm);
        setMessage('Turma criada com sucesso.');
      }
      setClassroomForm({
        name: '',
        description: '',
        status: 'ACTIVE',
        coordinatorUserId: user?.id || null,
        studentIds: [],
        courseIds: [],
      });
      setEditingClassroomId(null);
      await loadCoordinatorData();
    } catch (error) {
      setMessage(error?.response?.data?.error || `Não foi possível ${editingClassroomId ? 'atualizar' : 'criar'} turma.`);
    }
  };

  const startEditClassroom = (classroom) => {
    setEditingClassroomId(classroom.id);
    setClassroomForm({
      name: classroom.name || '',
      description: classroom.description || '',
      status: classroom.status === 'INACTIVE' ? 'INACTIVE' : 'ACTIVE',
      coordinatorUserId: classroom.coordinatorUserId || user?.id || null,
      studentIds: Array.isArray(classroom.studentIds) ? classroom.studentIds : [],
      courseIds: Array.isArray(classroom.courseIds) ? classroom.courseIds : [],
    });
  };

  const cancelEditClassroom = () => {
    setEditingClassroomId(null);
    setClassroomForm({
      name: '',
      description: '',
      status: 'ACTIVE',
      coordinatorUserId: user?.id || null,
      studentIds: [],
      courseIds: [],
    });
  };

  const deleteClassroom = async (classroom) => {
    const confirmed = window.confirm(`Deseja excluir a turma "${classroom.name}"?`);
    if (!confirmed) return;
    try {
      await api.delete(`/courses/classrooms/${classroom.id}`);
      if (editingClassroomId === classroom.id) {
        cancelEditClassroom();
      }
      setMessage('Turma excluída com sucesso.');
      await loadCoordinatorData();
    } catch (error) {
      setMessage(error?.response?.data?.error || 'Não foi possível excluir turma.');
    }
  };

  const consultCertificate = async () => {
    try {
      const result = await CourseService.consultCertificate(certificateQuery.trim());
      setCertificateResult(result || null);
      setMessage('');
    } catch (error) {
      setCertificateResult(null);
      setMessage(error?.response?.data?.error || 'Não foi possível consultar certificado.');
    }
  };

  const grantDirectAccess = async () => {
    try {
      if (!grantForm.courseId || !grantForm.userId) {
        setMessage('Selecione curso e aluno para conceder acesso.');
        return;
      }
      await api.post(`/courses/${grantForm.courseId}/grants`, {
        userId: grantForm.userId,
        accessStartAt: grantForm.accessStartAt || null,
        accessEndAt: grantForm.accessEndAt || null,
      });
      setMessage('Acesso direto concedido com sucesso.');
      setGrantForm({ courseId: null, userId: null, accessStartAt: '', accessEndAt: '' });
    } catch (error) {
      setMessage(error?.response?.data?.error || 'Não foi possível conceder acesso direto.');
    }
  };

  const saveLearningPath = () => {
    if (!learningPathForm.name.trim()) {
      setMessage('Informe um nome para a trilha de ensino.');
      return;
    }
    if (!learningPathForm.courseIds.length) {
      setMessage('Selecione ao menos um curso para compor a trilha.');
      return;
    }
    const nextPath = {
      id: editingLearningPathId || `path-${Date.now()}`,
      name: learningPathForm.name.trim(),
      description: learningPathForm.description.trim(),
      audience: learningPathForm.audience.trim(),
      courseIds: learningPathForm.courseIds,
    };
    setLearningPaths((prev) =>
      editingLearningPathId ? prev.map((path) => (path.id === editingLearningPathId ? nextPath : path)) : [...prev, nextPath],
    );
    setSelectedLearningPathId(nextPath.id);
    setEditingLearningPathId(null);
    setLearningPathForm({ name: '', description: '', audience: '', courseIds: [] });
    setMessage(editingLearningPathId ? 'Trilha de ensino atualizada com sucesso.' : 'Trilha de ensino criada com sucesso.');
  };

  const startEditLearningPath = (path) => {
    setEditingLearningPathId(path.id);
    setSelectedLearningPathId(path.id);
    setLearningPathForm({
      name: path.name || '',
      description: path.description || '',
      audience: path.audience || '',
      courseIds: Array.isArray(path.courseIds) ? [...path.courseIds] : [],
    });
  };

  const cancelLearningPathEdit = () => {
    setEditingLearningPathId(null);
    setLearningPathForm({ name: '', description: '', audience: '', courseIds: [] });
  };

  const deleteLearningPath = (pathId) => {
    setLearningPaths((prev) => prev.filter((path) => path.id !== pathId));
    if (editingLearningPathId === pathId) {
      cancelLearningPathEdit();
    }
    if (selectedLearningPathId === pathId) {
      setSelectedLearningPathId(null);
    }
    setMessage('Trilha de ensino removida com sucesso.');
  };

  const tabs = [
    {
      id: TABS.CATALOG,
      label: 'Catálogo do aluno',
      icon: FaUserGraduate,
      visible: true,
    },
    {
      id: TABS.ENROLLED,
      label: 'Cursos Matriculados',
      icon: FaBookOpen,
      visible: true,
    },
    {
      id: TABS.LEARNING_PATHS,
      label: 'Trilhas de Ensino',
      icon: FaProjectDiagram,
      visible: true,
    },
    {
      id: TABS.CREATOR,
      label: 'Autor / Editor de cursos',
      icon: FaChalkboardTeacher,
      visible: canViewEditor,
    },
    {
      id: TABS.COORDINATOR,
      label: 'Coordenação de turmas',
      icon: FaUsersCog,
      visible: canCoordinate,
    },
    {
      id: TABS.TEACHER,
      label: 'Atividades recebidas',
      icon: FaListOl,
      visible: canReviewActivities,
    },
    {
      id: TABS.CLASS_DIARY,
      label: 'Diário de Classe',
      icon: FaClipboardCheck,
      visible: canAccessClassDiary,
    },
    {
      id: TABS.CERTIFICATE,
      label: 'Consulta de certificados',
      icon: FaBookOpen,
      visible: true,
    },
  ].filter((tab) => tab.visible);

  return (
    <div className="space-y-5 rounded-[2rem] bg-gradient-to-b from-slate-100 via-blue-50/60 to-white p-3 md:p-5">
      <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-[0_24px_70px_-34px_rgba(15,23,42,0.4)]">
        <div className="bg-gradient-to-r from-slate-900 via-slate-800 to-blue-900 px-5 py-7 md:px-8">
          <h1 className="text-2xl font-semibold tracking-tight text-white md:text-3xl">Central Acadêmica • Módulo de Cursos</h1>
          <p className="mt-1 text-sm text-blue-100">
            Plataforma EAD institucional para alunos, professores e coordenação.
          </p>
        </div>
        <div className="grid grid-cols-2 gap-3 bg-white px-4 py-4 md:grid-cols-4 md:px-8">
          {dashboardHighlights.map((entry, index) => {
            const tones = [
              'border-blue-700 bg-gradient-to-br from-blue-700 via-blue-600 to-indigo-700 text-white',
              'border-violet-700 bg-gradient-to-br from-violet-700 via-purple-600 to-fuchsia-700 text-white',
              'border-emerald-700 bg-gradient-to-br from-emerald-700 via-teal-600 to-cyan-700 text-white',
              'border-amber-600 bg-gradient-to-br from-amber-500 via-orange-500 to-rose-600 text-white',
            ];
            return (
              <div
                key={entry.label}
                className={`rounded-2xl border p-3 shadow-[0_10px_25px_-15px_rgba(15,23,42,0.8)] ${tones[index % tones.length]}`}
              >
                <p className="text-xs font-semibold uppercase tracking-wide text-white/85">{entry.label}</p>
                <p className="text-2xl font-bold text-white">{entry.value}</p>
              </div>
            );
          })}
        </div>
      </div>

      {message ? (
        <div className="fixed left-1/2 top-6 z-50 w-[92%] max-w-xl -translate-x-1/2 rounded-xl border border-blue-300 bg-white px-4 py-3 text-sm text-blue-900 shadow-lg">
          <div className="flex items-center justify-between gap-2">
            <span>{message}</span>
            <button type="button" className="rounded border px-2 py-0.5 text-xs" onClick={() => setMessage('')}>Fechar</button>
          </div>
        </div>
      ) : null}

      <div className="flex flex-wrap gap-2 rounded-2xl border border-slate-200 bg-white p-2 shadow-sm">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          const active = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={`inline-flex items-center gap-2 rounded-xl border px-4 py-2 text-sm font-medium transition ${
                active
                  ? 'border-blue-700 bg-gradient-to-r from-blue-700 to-blue-600 text-white shadow'
                  : 'border-transparent bg-slate-100 text-slate-700 hover:border-blue-200 hover:bg-white hover:text-blue-700'
              }`}
            >
              <Icon />
              {tab.label}
            </button>
          );
        })}
      </div>

      {activeTab === TABS.CATALOG ? (
        <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
          <h2 className="mb-3 flex items-center gap-2 text-lg font-semibold text-slate-800">
            <FaBookOpen className="text-blue-600" /> Catálogo para Alunos
            <FaInfoCircle className="text-slate-400" title="Explore cursos, pré-requisitos e status de matrícula." />
          </h2>
          {loading ? <p className="text-sm text-slate-500">Carregando cursos...</p> : null}
          <div className="mb-3 flex flex-wrap gap-2">
            <button type="button" className={`rounded-lg px-3 py-1 text-xs ${catalogFilter === 'all' ? 'bg-blue-600 text-white' : 'border'}`} onClick={() => setCatalogFilter('all')}>
              Todos
            </button>
            <button type="button" className={`rounded-lg px-3 py-1 text-xs ${catalogFilter === 'enrolled' ? 'bg-blue-600 text-white' : 'border'}`} onClick={() => setCatalogFilter('enrolled')}>
              Matriculados
            </button>
            <button type="button" className={`rounded-lg px-3 py-1 text-xs ${catalogFilter === 'not-enrolled' ? 'bg-blue-600 text-white' : 'border'}`} onClick={() => setCatalogFilter('not-enrolled')}>
              Não matriculados
            </button>
          </div>
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 2xl:grid-cols-3">
              {filteredCatalogCourses.map((course) => (
                <div key={course.id} className="rounded-2xl border border-slate-200 bg-slate-50/60 p-4 shadow-sm">
                  <div className="mb-2 flex items-start justify-between gap-2">
                    <button
                      type="button"
                      className="line-clamp-2 text-left text-base font-semibold text-slate-800 hover:text-blue-700"
                      onClick={() => {
                        setSelectedCourseId(course.id);
                        setSelectedCourseDetailTab('trilha');
                      }}
                    >
                      {course.title}
                    </button>
                    <span className="rounded-full bg-slate-100 px-2 py-1 text-[10px] font-semibold uppercase text-slate-600">
                      {course.status}
                    </span>
                  </div>
                  {course.coverImageUrl ? (
                    <img
                      src={resolveAssetUrl(course.coverImageUrl)}
                      alt={`Capa do curso ${course.title}`}
                      className="mt-2 aspect-[16/9] w-full rounded-xl object-cover"
                    />
                  ) : null}
                  <p className="mt-3 text-sm leading-relaxed text-slate-600">{course.description || 'Descrição não informada.'}</p>
                  <p className="mt-1 text-xs text-slate-500">
                    Acesso por turma/liberação: {course.requiresAccessGrant ? 'obrigatório' : 'livre'}
                  </p>
                  <p className="text-xs text-slate-500">
                    Duração total: {Math.round(getCourseDurationMinutes(course) / 60)}h ({getCourseDurationMinutes(course)} min)
                  </p>
                  {(course.prerequisiteCourseIds || []).length > 0 ? (
                    <p className="text-xs text-slate-500">Pré-requisitos: {(course.prerequisiteCourseIds || []).length} curso(s)</p>
                  ) : null}
                  <div className="mt-2 flex flex-wrap gap-2">
                    {enrolledCourseIds.includes(course.id) ? (
                      <button
                        type="button"
                        className="rounded-lg bg-red-600 px-3 py-1 text-xs font-medium text-white"
                        onClick={() => unenrollCourse(course.id)}
                      >
                        Desmatricular
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="rounded-lg bg-blue-600 px-3 py-1 text-xs font-medium text-white"
                        onClick={() => enrollCourse(course.id)}
                      >
                        Matricular
                      </button>
                    )}
                    {canCreate ? (
                      <button
                        type="button"
                        className="inline-flex items-center gap-1 rounded-lg border border-slate-300 px-3 py-1 text-xs"
                        onClick={() => startEditCourse(course)}
                      >
                        <FaEdit /> Editar
                      </button>
                    ) : null}
                    {canCreate && course.status !== 'published' ? (
                      <button
                        type="button"
                        className="rounded-lg bg-emerald-600 px-3 py-1 text-xs font-medium text-white"
                        onClick={() => publishCourse(course.id)}
                      >
                        Publicar
                      </button>
                    ) : null}
                  </div>
                </div>
              ))}
              {!filteredCatalogCourses.length ? (
                <p className="text-sm text-slate-500">Nenhum curso encontrado para este filtro.</p>
              ) : null}
          </div>
        </section>
      ) : null}

      {activeTab === TABS.ENROLLED ? (
        <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
          <h2 className="mb-3 flex items-center gap-2 text-lg font-semibold text-slate-800">
            <FaListOl className="text-blue-600" /> Cursos Matriculados
            <FaInfoCircle className="text-slate-400" title="Acompanhe o progresso aula por aula e envie atividades." />
          </h2>
          <div className="space-y-4">
            <div className="flex snap-x snap-mandatory gap-3 overflow-x-auto pb-1 md:grid md:grid-cols-2 md:overflow-visible md:pb-0 xl:grid-cols-3">
              {enrolledCourses.map((course) => (
                <button
                  key={course.id}
                  type="button"
                  onClick={() => {
                    setSelectedCourseId(course.id);
                    setSelectedCourseDetailTab('trilha');
                    setSelectedLessonId(null);
                    setIsLearningOverlayOpen(true);
                  }}
                  className={`min-w-[260px] snap-start rounded-2xl border p-3 text-left transition sm:p-4 md:min-w-0 ${selectedCourseId === course.id ? 'border-blue-600 bg-gradient-to-br from-blue-50 to-white shadow' : 'border-slate-200 bg-white hover:border-blue-200'}`}
                >
                  {course.coverImageUrl ? (
                    <img
                      src={resolveAssetUrl(course.coverImageUrl)}
                      alt={`Capa do curso ${course.title}`}
                      className="mb-2 aspect-[16/9] w-full rounded-lg object-cover"
                    />
                  ) : null}
                  <p className="font-semibold text-slate-800">{course.title}</p>
                  <p className="line-clamp-2 text-xs text-slate-500">{course.description}</p>
                </button>
              ))}
            </div>
            {!enrolledCourses.length ? (
              <p className="text-sm text-slate-500">Você ainda não está matriculado em cursos.</p>
            ) : null}
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
              {selectedCourse && enrolledCourseIds.includes(selectedCourse.id) ? (
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm font-semibold text-slate-800">Curso selecionado: {selectedCourse.title}</p>
                    <p className="text-xs text-slate-600">Toque em "Abrir modo aula" para estudar em tela sobreposta.</p>
                  </div>
                  <button
                    type="button"
                    className="min-h-11 rounded-xl bg-blue-600 px-4 py-2 text-sm font-semibold text-white"
                    onClick={() => setIsLearningOverlayOpen(true)}
                  >
                    Abrir modo aula
                  </button>
                </div>
              ) : (
                <p className="text-sm text-slate-500">Selecione um curso matriculado para abrir o modo aula.</p>
              )}
            </div>
          </div>
        </section>
      ) : null}

      {isLearningOverlayOpen && selectedCourse && enrolledCourseIds.includes(selectedCourse.id) ? (
        <div className="fixed inset-0 z-50 bg-slate-950/60 p-0 sm:p-4">
          <div className="flex h-full w-full flex-col overflow-hidden bg-white sm:mx-auto sm:max-h-[94vh] sm:max-w-6xl sm:rounded-3xl sm:shadow-2xl">
            <div className="sticky top-0 z-10 border-b border-slate-200 bg-white/95 px-4 py-3 backdrop-blur sm:px-5">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold text-slate-800 sm:text-base">Modo aula · {selectedCourse.title}</p>
                  <p className="text-xs text-slate-500">Experiência imersiva para assistir no celular e desktop.</p>
                </div>
                <button type="button" className="rounded-lg border border-slate-300 px-3 py-2 text-xs font-semibold text-slate-700" onClick={() => setIsLearningOverlayOpen(false)}>
                  Fechar
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-3 sm:p-5">
              <div className="rounded-2xl border border-slate-200 p-4">
                <h3 className="mb-2 flex items-center gap-2 font-semibold text-slate-800">
                  <FaListOl className="text-blue-600" /> Sequência: {selectedCourse.title}
                </h3>
                <div className="mb-3 mt-2 h-2 w-full overflow-hidden rounded-full bg-slate-100">
                  <div
                    className="h-full bg-blue-600 transition-all"
                    style={{ width: `${selectedCourseProgress?.progress?.percentage || 0}%` }}
                  />
                </div>
                <p className="mb-3 text-xs text-slate-600">
                  Progresso: {selectedCourseProgress?.progress?.completedLessons || 0}/
                  {selectedCourseProgress?.progress?.totalLessons || 0} aulas •{' '}
                  {selectedCourseProgress?.progress?.completedDurationMinutes || 0}/
                  {selectedCourseProgress?.progress?.totalDurationMinutes || getCourseDurationMinutes(selectedCourse)} min
                </p>
                {(selectedCourseProgress?.progress?.percentage || 0) >= 100 ? (
                  <div className="mb-3 rounded-lg border border-emerald-300 bg-emerald-50 p-3">
                    <p className="text-sm font-semibold text-emerald-800">Parabéns! Você concluiu este curso com sucesso.</p>
                    <button
                      type="button"
                      className="mt-2 rounded bg-emerald-600 px-3 py-1 text-xs font-semibold text-white"
                      onClick={() => generateCertificate(selectedCourse)}
                    >
                      Gerar certificado de conclusão
                    </button>
                  </div>
                ) : null}

                <div className="mb-3 grid grid-cols-2 gap-2 sm:flex sm:flex-wrap">
                  <button
                    type="button"
                    className={`rounded-lg px-2 py-2 text-xs font-semibold ${selectedCourseDetailTab === 'trilha' ? 'bg-blue-600 text-white' : 'border bg-white'}`}
                    onClick={() => setSelectedCourseDetailTab('trilha')}
                  >
                    Trilha completa
                  </button>
                  <button
                    type="button"
                    className={`rounded-lg px-2 py-2 text-xs font-semibold ${selectedCourseDetailTab === 'concluidas' ? 'bg-blue-600 text-white' : 'border bg-white'}`}
                    onClick={() => setSelectedCourseDetailTab('concluidas')}
                  >
                    Aulas concluídas
                  </button>
                </div>

                {selectedCourseDetailTab === 'trilha' ? (
                  <div className="space-y-4">
                    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3">
                      <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-600">Escolha rápida da aula</p>
                      <select
                        value={selectedLessonId || currentLesson?.id || ''}
                        onChange={(event) => setSelectedLessonId(event.target.value)}
                        className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700"
                      >
                        {orderedLessons.map((lesson, index) => {
                          const locked = !unlockedLessonIds.has(lesson.id);
                          return (
                            <option key={`lesson-selector-${lesson.id}`} value={lesson.id} disabled={locked}>
                              {`Aula ${index + 1} • ${lesson.title}${locked ? ' (bloqueada)' : ''}`}
                            </option>
                          );
                        })}
                      </select>
                    </div>

                    {currentLesson ? (
                      <div className={`rounded-2xl border p-3 sm:p-4 ${unlockedLessonIds.has(currentLesson.id) ? 'border-slate-200 bg-slate-50' : 'border-amber-200 bg-amber-50'}`}>
                        <div className="mb-4 space-y-3 rounded-xl border border-slate-200 bg-white/90 p-3">
                          <div>
                            <p className="text-xs font-semibold text-slate-500">Módulo {currentLesson.moduleOrder}: {currentLesson.moduleTitle}</p>
                            <p className="text-base font-semibold text-slate-800 sm:text-lg">Aula {currentLessonIndex + 1} · {currentLesson.title}</p>
                            <p className="text-sm text-slate-600">{currentLesson.description}</p>
                          </div>
                          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                            <button
                              type="button"
                              onClick={() => previousLesson && setSelectedLessonId(previousLesson.id)}
                              disabled={!previousLesson}
                              className="inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700 transition hover:border-blue-300 hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
                              aria-label="Aula anterior"
                            >
                              <FaChevronLeft />
                              <span>Aula anterior</span>
                            </button>
                            <button
                              type="button"
                              onClick={() => nextLesson && unlockedLessonIds.has(nextLesson.id) && setSelectedLessonId(nextLesson.id)}
                              disabled={!nextLesson || !unlockedLessonIds.has(nextLesson.id)}
                              className="inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-xl border border-blue-600 bg-blue-600 px-3 py-2 text-sm font-semibold text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:border-slate-300 disabled:bg-slate-300"
                              aria-label="Próxima aula"
                            >
                              <span>Próxima aula</span>
                              <FaChevronRight />
                            </button>
                          </div>
                        </div>
                        {!unlockedLessonIds.has(currentLesson.id) ? (
                          <p className="rounded-lg border border-amber-200 bg-amber-100 p-3 text-sm font-medium text-amber-800">Conclua a aula anterior para visualizar o conteúdo desta aula.</p>
                        ) : (
                          <>
                            <div className="space-y-3">
                              <div className="rounded-xl border border-slate-200 bg-white p-3">
                                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-600">Conteúdo da aula</p>
                                {(currentLesson.materials || []).length > 0 ? (
                                  <div className="space-y-3">
                                    {(currentLesson.materials || []).map((material) => {
                                      const materialUrl = resolveAssetUrl(material.url || material.downloadUrl);
                                      return (
                                        <div key={`preview-${material.id}`} className="rounded-xl border border-slate-200 bg-slate-50 p-2 sm:p-3">
                                          <p className="mb-2 truncate text-sm font-semibold text-slate-700">{material.name}</p>
                                          {isYoutubeMaterial(material) ? (
                                            <div className="aspect-video w-full overflow-hidden rounded bg-black">
                                              <iframe
                                                src={getYouTubeEmbedUrl(material)}
                                                title={material.name || 'Vídeo do YouTube'}
                                                className="h-full w-full"
                                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                                referrerPolicy="strict-origin-when-cross-origin"
                                                allowFullScreen
                                              />
                                            </div>
                                          ) : null}
                                          {!isYoutubeMaterial(material) && isVideoMaterial(material) ? (
                                            <video controls playsInline className="max-h-[50vh] w-full rounded bg-black object-contain sm:max-h-[70vh]">
                                              <source src={materialUrl} />
                                              Seu navegador não suporta vídeo.
                                            </video>
                                          ) : null}
                                          {isImageMaterial(material) ? (
                                            <img src={materialUrl} alt={material.name} className="max-h-[50vh] w-full rounded object-contain bg-white sm:max-h-[70vh]" />
                                          ) : null}
                                          {isPdfMaterial(material) ? (
                                            <iframe
                                              src={materialUrl}
                                              title={`Prévia ${material.name}`}
                                              className="h-[55vh] min-h-[280px] w-full rounded border border-slate-200 bg-white sm:h-[70vh] sm:min-h-[420px]"
                                            />
                                          ) : null}
                                          {!isYoutubeMaterial(material) && !isVideoMaterial(material) && !isImageMaterial(material) && !isPdfMaterial(material) ? (
                                            <div className="rounded border border-dashed border-slate-300 bg-white p-3 text-xs text-slate-600">
                                              Prévia indisponível para este tipo de arquivo. Use o link abaixo para abrir/download.
                                            </div>
                                          ) : null}
                                          <a href={materialUrl} target="_blank" rel="noreferrer" className="mt-2 inline-flex rounded-lg bg-blue-50 px-3 py-2 text-xs font-semibold text-blue-700 hover:bg-blue-100">
                                            Abrir material
                                          </a>
                                        </div>
                                      );
                                    })}
                                  </div>
                                ) : (
                                  <p className="rounded-lg border border-dashed border-slate-300 bg-slate-50 p-3 text-xs text-slate-600">
                                    Esta aula ainda não possui materiais anexados.
                                  </p>
                                )}
                              </div>

                              {(currentLesson.tasks || []).length ? (
                                <div className="rounded-xl border border-amber-200 bg-amber-50 p-3">
                                  <p className="mb-2 text-sm font-semibold text-amber-800">Tarefas da aula</p>
                                  <div className="space-y-3">
                                    {(currentLesson.tasks || []).map((task) => (
                                      <div key={`task-${task.id}`} className="rounded-xl border border-amber-200 bg-white p-3">
                                        <p className="text-sm font-semibold text-amber-900">{task.title}</p>
                                        <p className="mt-1 text-xs text-amber-700">{task.instructions || task.description}</p>
                                        {(task.formQuestions || []).map((question, questionIndex) => (
                                          <div key={`task-q-${task.id}-${question.id || questionIndex}`} className="mt-3 text-xs">
                                            <p className="mb-1 font-semibold text-slate-700">Pergunta {questionIndex + 1}</p>
                                            <p className="mb-1 text-slate-600">{question.prompt}</p>
                                            {question.type === 'multiple_choice' ? (
                                              <select className="min-h-11 w-full rounded-lg border border-slate-300 p-2 text-sm" value={String((taskAnswers[task.id] || [])[questionIndex] ?? '')} onChange={(event) => updateTaskAnswer(task.id, questionIndex, Number(event.target.value))}>
                                                <option value="">Selecione</option>
                                                {(question.options || []).map((option, optionIndex) => <option key={`task-opt-${task.id}-${optionIndex}`} value={optionIndex}>{option}</option>)}
                                              </select>
                                            ) : null}
                                            {question.type === 'text' ? <textarea className="min-h-24 w-full rounded-lg border border-slate-300 p-2 text-sm" value={String((taskAnswers[task.id] || [])[questionIndex] ?? '')} onChange={(event) => updateTaskAnswer(task.id, questionIndex, event.target.value)} /> : null}
                                            {question.type === 'attachment' ? <input type="file" className="w-full rounded-lg border border-dashed border-slate-300 bg-white p-2 text-xs file:mr-3 file:rounded file:border-0 file:bg-amber-100 file:px-3 file:py-2 file:text-xs file:font-semibold file:text-amber-800" onChange={async (event) => { const file = event.target.files?.[0]; if (!file) return; const url = await uploadAnswerAttachment(file); updateTaskAnswer(task.id, questionIndex, url); }} /> : null}
                                          </div>
                                        ))}
                                        <button type="button" className="mt-3 min-h-11 w-full rounded-xl bg-amber-600 px-4 py-2 text-sm font-semibold text-white" onClick={() => submitTaskForm(selectedCourse.id, currentLesson.id, task)}>Enviar tarefa</button>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              ) : null}

                              {currentLesson.quiz ? (
                                <div className="rounded-xl border border-purple-200 bg-purple-50 p-3">
                                  <p className="mb-2 text-sm font-semibold text-purple-800">Avaliação: {currentLesson.quiz.title}</p>
                                  {(currentLesson.quiz.questions || []).map((question, questionIndex) => (
                                    <div key={`quiz-q-${currentLesson.id}-${question.id || questionIndex}`} className="mt-3 text-xs">
                                      <p className="mb-1 font-semibold text-slate-700">Questão {questionIndex + 1}</p>
                                      <p className="mb-1 text-slate-600">{question.prompt}</p>
                                      {question.type === 'multiple_choice' ? <select className="min-h-11 w-full rounded-lg border border-slate-300 p-2 text-sm" value={String((quizAnswers[currentLesson.id] || [])[questionIndex] ?? '')} onChange={(event) => updateQuizAnswer(currentLesson.id, questionIndex, Number(event.target.value))}><option value="">Selecione</option>{(question.options || []).map((option, optionIndex) => <option key={`quiz-opt-${currentLesson.id}-${optionIndex}`} value={optionIndex}>{option}</option>)}</select> : null}
                                      {question.type === 'text' ? <textarea className="min-h-24 w-full rounded-lg border border-slate-300 p-2 text-sm" value={String((quizAnswers[currentLesson.id] || [])[questionIndex] ?? '')} onChange={(event) => updateQuizAnswer(currentLesson.id, questionIndex, event.target.value)} /> : null}
                                      {question.type === 'attachment' ? <input type="file" className="w-full rounded-lg border border-dashed border-slate-300 bg-white p-2 text-xs file:mr-3 file:rounded file:border-0 file:bg-purple-100 file:px-3 file:py-2 file:text-xs file:font-semibold file:text-purple-800" onChange={async (event) => { const file = event.target.files?.[0]; if (!file) return; const url = await uploadAnswerAttachment(file); updateQuizAnswer(currentLesson.id, questionIndex, url); }} /> : null}
                                    </div>
                                  ))}
                                  <button type="button" className="mt-3 min-h-11 w-full rounded-xl bg-purple-700 px-4 py-2 text-sm font-semibold text-white" onClick={() => submitQuizForm(selectedCourse.id, currentLesson.id)}>Enviar avaliação</button>
                                </div>
                              ) : null}

                              <div className="sticky bottom-2 z-10 rounded-xl border border-slate-200 bg-white/95 p-2 shadow-sm backdrop-blur sm:static sm:border-0 sm:bg-transparent sm:p-0 sm:shadow-none">
                                {selectedCourseProgress?.enrollment?.completedLessonIds?.includes(currentLesson.id) ? (
                                  <button
                                    type="button"
                                    className="min-h-11 w-full rounded-xl bg-slate-700 px-4 py-2 text-sm font-semibold text-white"
                                    onClick={() => markLessonAsNotDone(selectedCourse.id, currentLesson.id)}
                                  >
                                    Reassistir aula
                                  </button>
                                ) : (
                                  <button
                                    type="button"
                                    className="min-h-11 w-full rounded-xl bg-green-600 px-4 py-2 text-sm font-semibold text-white"
                                    onClick={() => markLessonAsDone(selectedCourse.id, currentLesson.id)}
                                  >
                                    Concluir aula e liberar próxima
                                  </button>
                                )}
                              </div>
                            </div>
                          </>
                        )}
                      </div>
                    ) : null}

                    <div className="rounded-2xl border border-slate-200 p-3 sm:p-4">
                      <p className="mb-2 text-xs font-semibold text-slate-600">Aulas (resumo)</p>
                      <div className="grid grid-cols-1 gap-2 md:grid-cols-2 xl:grid-cols-3">
                        {orderedLessons.map((lesson, index) => {
                          const locked = !unlockedLessonIds.has(lesson.id);
                          const done = selectedCourseProgress?.enrollment?.completedLessonIds?.includes(lesson.id);
                          return (
                            <button
                              key={`lesson-summary-${lesson.id}`}
                              type="button"
                              disabled={locked}
                              onClick={() => setSelectedLessonId(lesson.id)}
                              className={`rounded-xl border p-3 text-left text-xs ${selectedLessonId === lesson.id ? 'border-blue-500 bg-blue-50' : 'border-slate-200 bg-white'} ${locked ? 'cursor-not-allowed opacity-60' : ''}`}
                            >
                              <div className="mb-1 flex items-center justify-between gap-2">
                                <p className="font-semibold text-slate-700">Aula {index + 1}</p>
                                <span className={`rounded-full px-2 py-1 text-[10px] font-semibold ${done ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>{done ? 'Concluída' : locked ? 'Bloqueada' : 'Disponível'}</span>
                              </div>
                              <p className="line-clamp-2 text-slate-700">{lesson.title}</p>
                              <p className="mt-1 text-slate-500">{lesson.durationMinutes || 30} min</p>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {(selectedCourse.modules || [])
                      .flatMap((module) => module.lessons || [])
                      .filter((lesson) =>
                        Boolean(
                          selectedCourseProgress?.enrollment?.completedLessonIds?.includes(
                            lesson.id,
                          ),
                        ),
                      )
                      .map((lesson) => (
                        <div key={lesson.id} className="rounded-lg border border-emerald-200 bg-emerald-50 p-2 text-sm">
                          <p className="font-medium text-emerald-800">
                            {lesson.title} ({lesson.durationMinutes || 30} min)
                          </p>
                          <p className="text-emerald-700">{lesson.description}</p>
                        </div>
                      ))}
                    {!selectedCourseProgress?.enrollment?.completedLessonIds?.length ? (
                      <p className="text-xs text-slate-500">Nenhuma aula concluída ainda.</p>
                    ) : null}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {activeTab === TABS.LEARNING_PATHS ? (
        <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
          <h2 className="mb-3 flex items-center gap-2 text-lg font-semibold text-slate-800">
            <FaProjectDiagram className="text-blue-600" /> Trilhas de Ensino
            <FaInfoCircle className="text-slate-400" title="Monte fluxos de crescimento conectando vários cursos." />
          </h2>
          <p className="mb-4 text-sm text-slate-600">
            Escolha uma trilha para visualizar o fluxo recomendado de cursos. Apenas editores de curso podem cadastrar, editar ou excluir trilhas.
          </p>

          {canManageLearningPaths ? (
            <div className="mb-4 rounded-2xl border border-blue-100 bg-blue-50/60 p-4">
              <h3 className="mb-3 text-sm font-semibold text-blue-800">
                {editingLearningPathId ? 'Editar trilha' : 'Cadastrar nova trilha'}
              </h3>
              <div className="grid grid-cols-1 gap-3 xl:grid-cols-2">
                <input
                  type="text"
                  placeholder="Nome da trilha"
                  value={learningPathForm.name}
                  className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
                  onChange={(event) => setLearningPathForm((prev) => ({ ...prev, name: event.target.value }))}
                />
                <input
                  type="text"
                  placeholder="Público-alvo (opcional)"
                  value={learningPathForm.audience}
                  className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
                  onChange={(event) => setLearningPathForm((prev) => ({ ...prev, audience: event.target.value }))}
                />
              </div>
              <textarea
                placeholder="Descrição da trilha (opcional)"
                value={learningPathForm.description}
                className="mt-3 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
                onChange={(event) => setLearningPathForm((prev) => ({ ...prev, description: event.target.value }))}
              />
              <div className="mt-3 grid grid-cols-1 gap-2 md:grid-cols-2">
                {courses.map((course) => {
                  const checked = learningPathForm.courseIds.includes(course.id);
                  return (
                    <label key={`path-course-${course.id}`} className={`cursor-pointer rounded-lg border px-3 py-2 text-sm ${checked ? 'border-blue-400 bg-blue-50 text-blue-800' : 'border-slate-200 bg-white text-slate-700'}`}>
                      <input
                        type="checkbox"
                        className="mr-2"
                        checked={checked}
                        onChange={() =>
                          setLearningPathForm((prev) => ({
                            ...prev,
                            courseIds: checked
                              ? prev.courseIds.filter((id) => id !== course.id)
                              : [...prev.courseIds, course.id],
                          }))
                        }
                      />
                      {course.title}
                    </label>
                  );
                })}
              </div>
              <button
                type="button"
                className="mt-3 rounded-lg bg-blue-700 px-3 py-2 text-sm font-medium text-white"
                onClick={saveLearningPath}
              >
                <FaPlus className="mr-2 inline" />
                {editingLearningPathId ? 'Salvar alterações' : 'Publicar trilha'}
              </button>
              {editingLearningPathId ? (
                <button
                  type="button"
                  className="ml-2 mt-3 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700"
                  onClick={cancelLearningPathEdit}
                >
                  Cancelar edição
                </button>
              ) : null}
            </div>
          ) : null}

          <div className="grid grid-cols-1 gap-3 xl:grid-cols-2">
            {learningPaths.map((path) => (
              <div
                key={path.id}
                className={`rounded-2xl border p-4 transition ${selectedLearningPath?.id === path.id ? 'border-blue-600 bg-gradient-to-br from-blue-50 to-white shadow' : 'border-slate-200 bg-white hover:border-blue-200'}`}
              >
                <button type="button" onClick={() => setSelectedLearningPathId(path.id)} className="w-full text-left">
                  <p className="text-base font-semibold text-slate-800">{path.name}</p>
                  <p className="text-xs text-slate-500">{path.description || 'Sem descrição cadastrada.'}</p>
                  <p className="mt-2 text-[11px] font-semibold uppercase tracking-wide text-blue-700">
                    {path.courseIds?.length || 0} curso(s) no fluxo
                  </p>
                </button>
                {canManageLearningPaths ? (
                  <div className="mt-3 flex items-center gap-2">
                    <button
                      type="button"
                      className="inline-flex items-center gap-1 rounded-lg border border-slate-300 bg-white px-3 py-1 text-xs text-slate-700"
                      onClick={() => startEditLearningPath(path)}
                    >
                      <FaEdit />
                      Editar
                    </button>
                    <button
                      type="button"
                      className="inline-flex items-center gap-1 rounded-lg border border-rose-200 bg-rose-50 px-3 py-1 text-xs text-rose-700"
                      onClick={() => deleteLearningPath(path.id)}
                    >
                      <FaTrash />
                      Excluir
                    </button>
                  </div>
                ) : null}
              </div>
            ))}
          </div>

          {selectedLearningPath ? (
            <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50/60 p-4">
              <h3 className="text-lg font-semibold text-slate-800">{selectedLearningPath.name}</h3>
              <p className="text-sm text-slate-600">
                {selectedLearningPath.description || 'Fluxo de aprendizagem definido para evolução contínua.'}
              </p>
              {selectedLearningPath.audience ? (
                <p className="mt-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Público recomendado: {selectedLearningPath.audience}
                </p>
              ) : null}
              <div className="mt-4 flex flex-wrap items-center gap-2">
                {(selectedLearningPath.courseIds || []).map((courseId, index) => {
                  const course = courses.find((entry) => entry.id === courseId);
                  return (
                    <React.Fragment key={`flow-${selectedLearningPath.id}-${courseId}`}>
                      <div className="rounded-xl border border-blue-200 bg-white px-3 py-2 shadow-sm">
                        <p className="text-xs font-semibold text-blue-900">{index + 1}º passo</p>
                        <p className="text-sm font-medium text-slate-800">{course?.title || 'Curso indisponível'}</p>
                      </div>
                      {index < selectedLearningPath.courseIds.length - 1 ? (
                        <FaArrowRight className="text-blue-400" />
                      ) : null}
                    </React.Fragment>
                  );
                })}
              </div>
            </div>
          ) : (
            <p className="mt-3 text-sm text-slate-500">Nenhuma trilha disponível no momento.</p>
          )}
        </section>
      ) : null}

      {activeTab === TABS.TEACHER && canReviewActivities ? (
        <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
          <h2 className="mb-3 flex items-center gap-2 text-lg font-semibold text-slate-800">
            <FaListOl className="text-blue-600" /> Atividades recebidas (Professor)
            <FaInfoCircle className="text-slate-400" title="Filtre por aluno, curso e turma para avaliação." />
          </h2>
          <div className="mb-3 grid grid-cols-1 gap-2 md:grid-cols-3">
            <select
              className="rounded border p-2 text-sm"
              value={instructorFilters.studentId}
              onChange={(event) => setInstructorFilters((prev) => ({ ...prev, studentId: event.target.value }))}
            >
              <option value="all">Todos os alunos</option>
              {Array.from(new Map(instructorSubmissions.map((entry) => [entry.studentId, entry.studentName])).entries()).map(([id, name]) => (
                <option key={`filter-student-${id}`} value={id}>{name}</option>
              ))}
            </select>
            <select
              className="rounded border p-2 text-sm"
              value={instructorFilters.courseId}
              onChange={(event) => setInstructorFilters((prev) => ({ ...prev, courseId: event.target.value }))}
            >
              <option value="all">Todos os cursos</option>
              {Array.from(new Map(instructorSubmissions.map((entry) => [entry.courseId, entry.courseTitle])).entries()).map(([id, title]) => (
                <option key={`filter-course-${id}`} value={id}>{title}</option>
              ))}
            </select>
            <select
              className="rounded border p-2 text-sm"
              value={instructorFilters.classroom}
              onChange={(event) => setInstructorFilters((prev) => ({ ...prev, classroom: event.target.value }))}
            >
              <option value="all">Todas as turmas</option>
              {Array.from(new Set(instructorSubmissions.flatMap((entry) => entry.classroomNames || []))).map((name) => (
                <option key={`filter-class-${name}`} value={name}>{name}</option>
              ))}
            </select>
          </div>
          <div className="space-y-2">
            {filteredInstructorSubmissions.length === 0 ? (
              <p className="text-sm text-slate-500">Nenhuma atividade recebida ainda.</p>
            ) : null}
            {filteredInstructorSubmissions.map((entry) => (
              <div key={`${entry.type}-${entry.courseId}-${entry.lessonId}-${entry.studentId}-${entry.submittedAt}`} className="rounded-lg border border-slate-200 p-3">
                <p className="text-sm font-semibold text-slate-800">{entry.courseTitle}</p>
                <p className="text-xs text-slate-600">
                  Aluno: {entry.studentName} • Aula: {entry.lessonTitle} • Tipo: {entry.type === 'task' ? 'Tarefa' : 'Avaliação'}
                </p>
                <p className="text-xs text-slate-500">Turma(s): {(entry.classroomNames || []).join(', ') || 'Sem turma vinculada'}</p>
                <p className="text-xs text-slate-500">Enviado em: {new Date(entry.submittedAt).toLocaleString('pt-BR')}</p>
                <p className="text-xs text-slate-600">Nota: {entry.score ?? 'N/A'}</p>
                {entry.attachmentUrl ? (
                  <a href={resolveAssetUrl(entry.attachmentUrl)} target="_blank" rel="noreferrer" className="text-xs text-blue-600 hover:underline">
                    Ver anexo enviado
                  </a>
                ) : null}
              </div>
            ))}
          </div>
        </section>
      ) : null}

      {activeTab === TABS.CLASS_DIARY && canAccessClassDiary ? (
        <section className="overflow-hidden rounded-3xl border border-slate-300 bg-slate-200 shadow-sm">
          <div className="bg-[#02043b] px-6 py-4 text-white">
            <h2 className="flex items-center justify-center gap-2 text-2xl font-bold">
              <FaClipboardCheck className="text-amber-300" /> Diário de Classe
            </h2>
          </div>

          <div className="grid grid-cols-1 gap-3 p-4 lg:grid-cols-2">
            <div className="rounded-lg border border-slate-300 bg-white p-3">
              <div className="mb-3 grid grid-cols-1 gap-2 md:grid-cols-3">
                <select
                  className="rounded border border-slate-400 p-2 text-sm"
                  value={attendanceClassroomId || ''}
                  onChange={(event) => setAttendanceClassroomId(Number(event.target.value) || null)}
                >
                  <option value="">Turma</option>
                  {attendanceClassrooms.map((classroom) => (
                    <option key={`attendance-class-${classroom.id}`} value={classroom.id}>
                      {classroom.name}
                    </option>
                  ))}
                </select>
                <input
                  className="rounded border border-slate-400 p-2 text-sm"
                  type="date"
                  value={attendanceDate}
                  onChange={(event) => setAttendanceDate(event.target.value)}
                />
                <button
                  type="button"
                  className="rounded border border-slate-400 bg-slate-100 px-3 py-2 text-xs font-semibold text-slate-800 disabled:opacity-60"
                  onClick={saveClassDiary}
                  disabled={!canEditAttendance}
                >
                  {editingDiaryId ? 'Atualizar Diário' : 'Criar Diário'}
                </button>
              </div>
              <div className="mb-3 space-y-2">
                <input
                  className="w-full rounded border border-slate-300 p-2 text-sm"
                  placeholder="Tema da aula"
                  value={diaryTheme}
                  onChange={(event) => setDiaryTheme(event.target.value)}
                  disabled={!canEditAttendance}
                />
                <input
                  className="w-full rounded border border-slate-300 p-2 text-sm"
                  placeholder="Objetivo"
                  value={diaryObjective}
                  onChange={(event) => setDiaryObjective(event.target.value)}
                  disabled={!canEditAttendance}
                />
              </div>
              <div className="mb-2 flex gap-2">
                <button
                  type="button"
                  className="rounded bg-sky-600 px-3 py-1 text-xs font-semibold text-white disabled:opacity-60"
                  disabled={!selectedDiaryId}
                  onClick={() => setIsRegisteringFrequency(true)}
                >
                  Registrar Frequência
                </button>
                <button
                  type="button"
                  className="rounded border border-slate-400 px-3 py-1 text-xs"
                  onClick={() => {
                    setEditingDiaryId(null);
                    setDiaryTheme('');
                    setDiaryObjective('');
                  }}
                >
                  Novo
                </button>
              </div>

              <div className="mb-3 max-h-40 overflow-auto rounded border border-slate-300">
                <table className="w-full text-xs">
                  <thead className="bg-slate-100">
                    <tr>
                      <th className="border border-slate-300 px-2 py-1 text-left">Data</th>
                      <th className="border border-slate-300 px-2 py-1 text-left">Tutor</th>
                      <th className="border border-slate-300 px-2 py-1 text-left">Tema</th>
                      <th className="border border-slate-300 px-2 py-1 text-left">Pres/Falt/Jus</th>
                      <th className="border border-slate-300 px-2 py-1 text-left">Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {classDiaries.map((diary) => (
                      <tr key={`diary-${diary.id}`} className={Number(selectedDiaryId) === Number(diary.id) ? 'bg-blue-50' : ''}>
                        <td className="border border-slate-300 px-2 py-1">{new Date(`${diary.diaryDate}T00:00:00Z`).toLocaleDateString('pt-BR')}</td>
                        <td className="border border-slate-300 px-2 py-1">{diary.tutorName}</td>
                        <td className="border border-slate-300 px-2 py-1">{diary.theme || '-'}</td>
                        <td className="border border-slate-300 px-2 py-1">{diary.presentCount}/{diary.absentCount}/{diary.justifiedCount}</td>
                        <td className="border border-slate-300 px-2 py-1">
                          <div className="flex gap-1">
                            <button type="button" className="rounded border px-2" onClick={() => startEditDiary(diary)}>Editar</button>
                            <button type="button" className="rounded border px-2 text-rose-700" onClick={() => removeDiary(diary.id)}>Excluir</button>
                            <button type="button" className="rounded border px-2 text-sky-700" onClick={() => { setSelectedDiaryId(diary.id); setIsRegisteringFrequency(true); }}>Freq.</button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="max-h-72 overflow-auto rounded border border-slate-400">
                <table className="w-full text-xs">
                  <thead className="bg-slate-200 text-slate-700">
                    <tr>
                      <th className="border border-slate-300 px-2 py-1 text-left">ID</th>
                      <th className="border border-slate-300 px-2 py-1 text-left">Nome</th>
                      <th className="border border-slate-300 px-2 py-1 text-left">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {attendanceRows.map((row) => {
                      const student = studentUsers.find((entry) => Number(entry.id) === Number(row.studentId));
                      const isSelected = Number(selectedAttendanceStudentId) === Number(row.studentId);
                      return (
                        <tr
                          key={`attendance-student-${row.studentId}`}
                          className={isSelected ? 'bg-blue-50' : 'bg-white'}
                          onClick={() => setSelectedAttendanceStudentId(row.studentId)}
                        >
                          <td className="cursor-pointer border border-slate-300 px-2 py-1">{row.studentId}</td>
                          <td className="cursor-pointer border border-slate-300 px-2 py-1">
                            {student?.name || `Aluno #${row.studentId}`}
                          </td>
                          <td className="border border-slate-300 px-2 py-1">{row.status}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                {attendanceRows.length === 0 ? (
                  <p className="p-3 text-sm text-slate-500">Nenhum aluno para a turma selecionada.</p>
                ) : null}
              </div>
            </div>

            <div className={`space-y-3 ${isRegisteringFrequency ? '' : 'opacity-50'}`}>
              <div className="rounded-lg bg-blue-400 p-4">
                <h3 className="mb-3 text-center text-3xl font-semibold text-[#02043b]">Registre a Frequência</h3>
                <div className="space-y-2 rounded border border-blue-300 bg-white px-3 py-2 text-sm">
                  <p><span className="font-semibold text-slate-700">Nome:</span> {selectedAttendanceStudent?.name || 'Selecione um aluno na lista'}</p>
                  <p><span className="font-semibold text-slate-700">ID Aluno:</span> {selectedAttendanceStudentId || '-'}</p>
                  <p><span className="font-semibold text-slate-700">Data:</span> {new Date(`${attendanceDate}T00:00:00Z`).toLocaleDateString('pt-BR')}</p>
                  <p><span className="font-semibold text-slate-700">Tutor:</span> {user?.name || '-'}</p>
                </div>
                <div className="mt-2 grid grid-cols-2 gap-2 rounded border border-blue-300 bg-white p-2 text-xs text-slate-700 md:grid-cols-5">
                  <label className="flex items-center gap-1">
                    <input
                      type="checkbox"
                      checked={selectedAttendanceRow?.allDay !== false}
                      disabled={!isRegisteringFrequency || !selectedAttendanceStudentId || !canEditAttendance}
                      onChange={(event) => updateAttendanceFieldForStudent(selectedAttendanceStudentId, 'allDay', event.target.checked)}
                    />
                    Dia todo
                  </label>
                  <span className="self-center text-center">Atrasou por</span>
                  <input
                    type="number"
                    min="0"
                    max="23"
                    className="rounded border border-slate-300 px-2 py-1"
                    value={Number(selectedAttendanceRow?.delayHours || 0)}
                    disabled={!isRegisteringFrequency || !selectedAttendanceStudentId || !canEditAttendance || selectedAttendanceRow?.allDay !== false}
                    onChange={(event) => updateAttendanceFieldForStudent(selectedAttendanceStudentId, 'delayHours', Number(event.target.value || 0))}
                  />
                  <input
                    type="number"
                    min="0"
                    max="59"
                    className="rounded border border-slate-300 px-2 py-1"
                    value={Number(selectedAttendanceRow?.delayMinutes || 0)}
                    disabled={!isRegisteringFrequency || !selectedAttendanceStudentId || !canEditAttendance || selectedAttendanceRow?.allDay !== false}
                    onChange={(event) => updateAttendanceFieldForStudent(selectedAttendanceStudentId, 'delayMinutes', Number(event.target.value || 0))}
                  />
                  <label className="flex items-center justify-end gap-1">
                    Obs Psic?
                    <input
                      type="checkbox"
                      checked={Boolean(selectedAttendanceRow?.psychologistObservation)}
                      disabled={!isRegisteringFrequency || !selectedAttendanceStudentId || !canEditAttendance}
                      onChange={(event) => updateAttendanceFieldForStudent(selectedAttendanceStudentId, 'psychologistObservation', event.target.checked)}
                    />
                  </label>
                </div>
                <div className="mt-2 grid grid-cols-1 gap-2 rounded border border-blue-300 bg-white p-2 text-xs text-slate-700 md:grid-cols-[1fr_auto]">
                  <input
                    className="rounded border border-slate-300 px-2 py-1"
                    placeholder="Observações do aluno"
                    value={selectedAttendanceRow?.studentObservation || ''}
                    disabled={!isRegisteringFrequency || !selectedAttendanceStudentId || !canEditAttendance}
                    onChange={(event) => updateAttendanceFieldForStudent(selectedAttendanceStudentId, 'studentObservation', event.target.value)}
                  />
                  <label className="flex items-center gap-1">
                    PCD?
                    <input
                      type="checkbox"
                      checked={Boolean(selectedAttendanceRow?.pcd)}
                      disabled={!isRegisteringFrequency || !selectedAttendanceStudentId || !canEditAttendance}
                      onChange={(event) => updateAttendanceFieldForStudent(selectedAttendanceStudentId, 'pcd', event.target.checked)}
                    />
                  </label>
                </div>
                <div className="mt-3 grid grid-cols-1 gap-2 md:grid-cols-3">
                  <button
                    type="button"
                    className="rounded bg-green-600 px-3 py-2 text-sm font-bold text-white disabled:opacity-50"
                    onClick={() => setAttendanceStatusForStudent(selectedAttendanceStudentId, 'PRESENTE')}
                    disabled={!isRegisteringFrequency || !selectedAttendanceStudentId || !canEditAttendance}
                  >
                    Presente
                  </button>
                  <button
                    type="button"
                    className="rounded bg-red-600 px-3 py-2 text-sm font-bold text-white disabled:opacity-50"
                    onClick={() => setAttendanceStatusForStudent(selectedAttendanceStudentId, 'FALTA')}
                    disabled={!isRegisteringFrequency || !selectedAttendanceStudentId || !canEditAttendance}
                  >
                    Ausente
                  </button>
                  <button
                    type="button"
                    className="rounded bg-blue-700 px-3 py-2 text-sm font-bold text-white disabled:opacity-50"
                    onClick={() => setAttendanceStatusForStudent(selectedAttendanceStudentId, 'JUSTIFICADO')}
                    disabled={!isRegisteringFrequency || !selectedAttendanceStudentId || !canEditAttendance}
                  >
                    Justificado
                  </button>
                </div>
                <div className="mt-2 text-right">
                  <button
                    type="button"
                    className="rounded bg-indigo-700 px-3 py-2 text-xs font-semibold text-white disabled:opacity-60"
                    onClick={saveAttendance}
                    disabled={!isRegisteringFrequency || !canEditAttendance || attendanceSaving}
                  >
                    {attendanceSaving ? 'Salvando...' : 'Salvar Frequência'}
                  </button>
                </div>
              </div>

              <div className="rounded-lg bg-blue-200 p-3">
                <h4 className="mb-2 text-lg font-semibold text-slate-800">Histórico de Frequência</h4>
                <div className="max-h-44 overflow-auto rounded border border-slate-300 bg-white">
                  <table className="w-full text-xs">
                    <thead className="bg-slate-100">
                      <tr>
                        <th className="border border-slate-300 px-2 py-1 text-left">Data</th>
                        <th className="border border-slate-300 px-2 py-1 text-left">Aluno</th>
                        <th className="border border-slate-300 px-2 py-1 text-left">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredAttendanceRecords.slice(0, 30).map((entry) => (
                        <tr key={`attendance-history-${entry.id}`}>
                          <td className="border border-slate-300 px-2 py-1">{new Date(`${entry.attendanceDate}T00:00:00Z`).toLocaleDateString('pt-BR')}</td>
                          <td className="border border-slate-300 px-2 py-1">{studentUsers.find((student) => student.id === entry.studentId)?.name || `Aluno #${entry.studentId}`}</td>
                          <td className="border border-slate-300 px-2 py-1">{entry.status}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>

          <div className="mx-4 mb-4 rounded-lg bg-[#02043b] p-4 text-white">
            <h3 className="text-center text-3xl font-semibold">Aba de Consulta</h3>
            <p className="mb-3 text-center text-xs text-slate-200">(Campos abaixo servem apenas para filtrar dados)</p>
            <div className="grid grid-cols-1 gap-2 md:grid-cols-5">
              <select className="rounded border border-slate-300 bg-white p-2 text-xs text-slate-700" value={attendanceFilters.classroomId} onChange={(event) => setAttendanceFilters((prev) => ({ ...prev, classroomId: event.target.value }))}>
                <option value="all">Turma</option>
                {attendanceClassrooms.map((classroom) => <option key={`attendance-filter-class-${classroom.id}`} value={classroom.id}>{classroom.name}</option>)}
              </select>
              <select className="rounded border border-slate-300 bg-white p-2 text-xs text-slate-700" value={attendanceFilters.studentId} onChange={(event) => setAttendanceFilters((prev) => ({ ...prev, studentId: event.target.value }))}>
                <option value="all">Nome do aluno</option>
                {studentUsers.map((student) => <option key={`attendance-filter-student-${student.id}`} value={student.id}>{student.name}</option>)}
              </select>
              <select className="rounded border border-slate-300 bg-white p-2 text-xs text-slate-700" value={attendanceFilters.teacherId} onChange={(event) => setAttendanceFilters((prev) => ({ ...prev, teacherId: event.target.value }))}>
                <option value="all">Professor</option>
                {users.map((teacher) => <option key={`attendance-filter-teacher-${teacher.id}`} value={teacher.id}>{teacher.name}</option>)}
              </select>
              <button type="button" className="rounded bg-sky-600 px-3 py-2 text-xs font-semibold text-white">Consultar</button>
              <div className="flex gap-2">
                <button type="button" className="flex-1 rounded border border-slate-300 bg-white px-3 py-2 text-xs font-semibold text-slate-800 disabled:opacity-60" disabled={attendanceExporting} onClick={exportAttendancePdf}>Relatório PDF</button>
                <button type="button" className="flex-1 rounded border border-slate-300 bg-white px-3 py-2 text-xs font-semibold text-slate-800 disabled:opacity-60" disabled={attendanceExporting} onClick={exportAttendanceXls}>Relatório XLS</button>
              </div>
            </div>
            <div className="mt-3 max-h-40 overflow-auto rounded border border-slate-300 bg-white text-xs text-slate-800">
              <table className="w-full">
                <thead className="bg-slate-100">
                  <tr>
                    <th className="border border-slate-300 px-2 py-1 text-left">Nome</th>
                    <th className="border border-slate-300 px-2 py-1 text-left">Data</th>
                    <th className="border border-slate-300 px-2 py-1 text-left">Tutor</th>
                    <th className="border border-slate-300 px-2 py-1 text-left">Turma</th>
                    <th className="border border-slate-300 px-2 py-1 text-left">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAttendanceRecords.slice(0, 100).map((entry) => (
                    <tr key={`attendance-query-${entry.id}`}>
                      <td className="border border-slate-300 px-2 py-1">{studentUsers.find((student) => student.id === entry.studentId)?.name || `Aluno #${entry.studentId}`}</td>
                      <td className="border border-slate-300 px-2 py-1">{new Date(`${entry.attendanceDate}T00:00:00Z`).toLocaleDateString('pt-BR')}</td>
                      <td className="border border-slate-300 px-2 py-1">{users.find((teacher) => teacher.id === entry.teacherId)?.name || `Professor #${entry.teacherId}`}</td>
                      <td className="border border-slate-300 px-2 py-1">{attendanceClassrooms.find((classroom) => classroom.id === entry.classroomId)?.name || `Turma #${entry.classroomId}`}</td>
                      <td className="border border-slate-300 px-2 py-1">{entry.status}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {!canEditAttendance ? (
              <p className="mt-2 text-xs text-amber-300">Você pode consultar, mas somente professor/admin pode registrar frequência.</p>
            ) : null}
          </div>
        </section>
      ) : null}

      {activeTab === TABS.CERTIFICATE ? (
        <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
          <h2 className="mb-3 text-lg font-semibold text-slate-800">Consulta de certificados</h2>
          <p className="text-xs text-slate-500">Validação institucional por ID de referência.</p>
          <p className="mb-2 text-xs text-slate-500">Página pública também disponível em: <code>/certificados/consulta</code></p>
          <div className="flex flex-wrap gap-2">
            <input
              className="min-w-[260px] flex-1 rounded border p-2 text-sm"
              placeholder="Informe o ID de referência (ex.: LMB-2026-000001)"
              value={certificateQuery}
              onChange={(event) => setCertificateQuery(event.target.value)}
            />
            <button
              type="button"
              className="rounded bg-blue-600 px-3 py-2 text-sm font-semibold text-white"
              onClick={consultCertificate}
            >
              Consultar
            </button>
          </div>
          {certificateResult ? (
            <div className="mt-3 rounded border border-slate-200 p-3 text-sm">
              <p><strong>Referência:</strong> {certificateResult.referenceId}</p>
              <p><strong>Aluno:</strong> {certificateResult.userName}</p>
              <p><strong>Curso:</strong> {certificateResult.courseTitle}</p>
              <p><strong>Carga horária:</strong> {certificateResult.estimatedHours}h</p>
              <p><strong>Emitido em:</strong> {new Date(certificateResult.issuedAt).toLocaleString('pt-BR')}</p>
            </div>
          ) : null}
        </section>
      ) : null}

      {activeTab === TABS.CREATOR && canViewEditor ? (
        <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
            <h2 className="flex items-center gap-2 text-lg font-semibold text-slate-800">
              <FaLayerGroup className="text-blue-600" />
              {editingCourseId ? 'Editar curso existente' : 'Criar novo curso'}
            </h2>
            <p className="text-xs text-slate-600">Professor responsável: {user?.name || 'Não informado'}</p>
            <div className="flex gap-2">
              <button
                type="button"
                className="inline-flex items-center gap-1 rounded-lg border border-slate-300 px-3 py-1 text-xs"
                onClick={resetCreatorForm}
              >
                <FaFolderPlus /> Novo curso
              </button>
              {canCreate ? (
                <button
                  type="button"
                  className="inline-flex items-center gap-1 rounded-lg bg-blue-600 px-3 py-1 text-xs font-semibold text-white"
                  onClick={saveCourse}
                  disabled={saving}
                >
                  <FaSave /> {saving ? 'Salvando...' : editingCourseId ? 'Salvar alterações' : 'Salvar curso'}
                </button>
              ) : null}
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
            <div className="rounded-xl border border-slate-200 p-3 xl:col-span-2">
              <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                <input className="rounded border p-2" placeholder="Título do curso" value={form.title} onChange={(event) => setForm((prev) => ({ ...prev, title: event.target.value }))} />
                <input
                  className="rounded border p-2"
                  type="text"
                  readOnly
                  value={`${getFormDurationMinutes(form)} minutos`}
                  title="Soma automática da duração de todas as aulas"
                />
              </div>
              <textarea className="mt-2 w-full rounded border p-2" placeholder="Descrição" value={form.description} onChange={(event) => setForm((prev) => ({ ...prev, description: event.target.value }))} />
              <input
                className="mt-2 w-full rounded border p-2 text-sm"
                type="email"
                placeholder="E-mail para receber atividades dos alunos"
                value={form.activityNotificationEmail || ''}
                onChange={(event) => setForm((prev) => ({ ...prev, activityNotificationEmail: event.target.value }))}
              />
              <div className="mt-2 rounded border p-2">
                <p className="mb-1 text-xs font-semibold text-slate-600">Imagem de destaque do curso</p>
                {form.coverImageUrl ? (
                  <img src={resolveAssetUrl(form.coverImageUrl)} alt="Capa do curso" className="mb-2 h-28 w-full rounded object-cover" />
                ) : null}
                <input
                  type="file"
                  accept="image/*"
                  onChange={async (event) => {
                    const file = event.target.files?.[0];
                    if (!file) return;
                    await uploadCourseCover(file);
                  }}
                />
              </div>
              <div className="mt-2 grid grid-cols-1 gap-2 md:grid-cols-2">
                <select className="rounded border p-2" value={form.difficulty} onChange={(event) => setForm((prev) => ({ ...prev, difficulty: event.target.value }))}>
                  <option value="iniciante">Iniciante</option>
                  <option value="intermediario">Intermediário</option>
                  <option value="avancado">Avançado</option>
                </select>
                <label className="flex items-center gap-2 rounded border p-2 text-xs">
                  <input type="checkbox" checked={form.requiresAccessGrant} onChange={(event) => setForm((prev) => ({ ...prev, requiresAccessGrant: event.target.checked }))} />
                  Exigir acesso por turma/liberação
                </label>
              </div>
              <div className="mt-2 rounded border p-2">
                <p className="mb-1 text-xs font-semibold text-slate-600">Trilha de cursos (pré-requisitos)</p>
                <div className="max-h-28 overflow-auto rounded border p-2 text-xs">
                  {courses
                    .filter((course) => course.id !== editingCourseId)
                    .map((course) => (
                      <label key={`pre-${course.id}`} className="mb-1 flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={(form.prerequisiteCourseIds || []).includes(course.id)}
                          onChange={() =>
                            setForm((prev) => ({
                              ...prev,
                              prerequisiteCourseIds: toggleMultiSelect(prev.prerequisiteCourseIds || [], course.id),
                            }))
                          }
                        />
                        {course.title}
                      </label>
                    ))}
                </div>
              </div>
              <div className="mt-2 rounded border p-2">
                <p className="mb-1 text-xs font-semibold text-slate-700">Configuração do certificado (administrador do curso)</p>
                <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                  <input
                    className="rounded border p-2 text-xs"
                    placeholder="Razão social"
                    value={form.certificateConfig?.companyName || ''}
                    onChange={(event) => setForm((prev) => ({ ...prev, certificateConfig: { ...(prev.certificateConfig || {}), companyName: event.target.value } }))}
                  />
                  <input
                    className="rounded border p-2 text-xs"
                    placeholder="CNPJ"
                    value={form.certificateConfig?.cnpj || ''}
                    onChange={(event) => setForm((prev) => ({ ...prev, certificateConfig: { ...(prev.certificateConfig || {}), cnpj: event.target.value } }))}
                  />
                  <input
                    className="rounded border p-2 text-xs"
                    placeholder="Endereço"
                    value={form.certificateConfig?.address || ''}
                    onChange={(event) => setForm((prev) => ({ ...prev, certificateConfig: { ...(prev.certificateConfig || {}), address: event.target.value } }))}
                  />
                  <input
                    className="rounded border p-2 text-xs"
                    placeholder="Contato"
                    value={form.certificateConfig?.contact || ''}
                    onChange={(event) => setForm((prev) => ({ ...prev, certificateConfig: { ...(prev.certificateConfig || {}), contact: event.target.value } }))}
                  />
                  <input
                    className="rounded border p-2 text-xs md:col-span-2"
                    placeholder="Nome de quem vai assinar o certificado"
                    value={form.certificateConfig?.signerName || ''}
                    onChange={(event) => setForm((prev) => ({ ...prev, certificateConfig: { ...(prev.certificateConfig || {}), signerName: event.target.value } }))}
                  />
                  <div className="rounded border p-2 text-xs">
                    <p className="mb-1 font-semibold text-slate-700">Cor do certificado</p>
                    <div className="flex items-center gap-2">
                      <input
                        className="h-8 w-12 cursor-pointer rounded border"
                        type="color"
                        value={form.certificateConfig?.primaryColor || '#0f766e'}
                        onChange={(event) => setForm((prev) => ({ ...prev, certificateConfig: { ...(prev.certificateConfig || {}), primaryColor: event.target.value } }))}
                        title="Cor principal do certificado"
                      />
                      <span
                        className="inline-block h-6 w-14 rounded border"
                        style={{ backgroundColor: form.certificateConfig?.primaryColor || '#0f766e' }}
                        title="Pré-visualização da cor"
                      />
                    </div>
                  </div>
                  <select
                    className="rounded border p-2 text-xs"
                    value={form.certificateConfig?.logoPosition || 'center'}
                    onChange={(event) => setForm((prev) => ({ ...prev, certificateConfig: { ...(prev.certificateConfig || {}), logoPosition: event.target.value } }))}
                  >
                    <option value="left">Logo à esquerda</option>
                    <option value="center">Logo centralizada</option>
                    <option value="right">Logo à direita</option>
                  </select>
                  <textarea
                    className="rounded border p-2 text-xs md:col-span-2"
                    rows={3}
                    placeholder="Texto do certificado (use {{ALUNO}}, {{CURSO}}, {{CARGA_HORARIA}})"
                    value={form.certificateConfig?.bodyTextTemplate || ''}
                    onChange={(event) => setForm((prev) => ({ ...prev, certificateConfig: { ...(prev.certificateConfig || {}), bodyTextTemplate: event.target.value } }))}
                  />
                </div>
                {form.certificateConfig?.logoUrl ? (
                  <img src={resolveAssetUrl(form.certificateConfig.logoUrl)} alt="Logo do certificado" className="mt-2 h-16 rounded object-contain" />
                ) : null}
                <p className="mt-2 text-xs font-semibold text-slate-700">Anexo: LOGO do certificado</p>
                <input
                  className="mt-1"
                  type="file"
                  accept="image/*"
                  onChange={async (event) => {
                    const file = event.target.files?.[0];
                    if (!file) return;
                    await uploadCertificateLogo(file);
                  }}
                />
              </div>

              {form.modules.map((module, moduleIndex) => (
                <div key={`module-${moduleIndex}`} className="mt-3 rounded-lg border border-slate-200 p-2">
                  <input className="mb-2 w-full rounded border p-2" placeholder={`Módulo ${moduleIndex + 1}`} value={module.title} onChange={(event) => updateModule(moduleIndex, { ...module, title: event.target.value })} />
                  {module.lessons.map((lesson, lessonIndex) => (
                    <div key={`lesson-${moduleIndex}-${lessonIndex}`} className="mb-2 rounded border border-slate-100 bg-slate-50 p-2">
                      <input className="mb-1 w-full rounded border p-2" placeholder={`Aula ${lessonIndex + 1}`} value={lesson.title} onChange={(event) => {
                        const lessons = module.lessons.map((entry, idx) => idx === lessonIndex ? { ...entry, title: event.target.value } : entry);
                        updateModule(moduleIndex, { ...module, lessons });
                      }} />
                      <textarea className="mb-1 w-full rounded border p-2" placeholder="Descrição da aula" value={lesson.description} onChange={(event) => {
                        const lessons = module.lessons.map((entry, idx) => idx === lessonIndex ? { ...entry, description: event.target.value } : entry);
                        updateModule(moduleIndex, { ...module, lessons });
                      }} />
                      <label className="mb-1 block text-[11px] font-semibold text-slate-600">Tempo da aula (em minutos)</label>
                      <input
                        className="mb-1 w-full rounded border p-2 text-xs"
                        type="number"
                        min="1"
                        placeholder="Ex.: 30 minutos"
                        value={lesson.durationMinutes || 30}
                        onChange={(event) => {
                          const minutes = Math.max(1, Number(event.target.value || 1));
                          const lessons = module.lessons.map((entry, idx) =>
                            idx === lessonIndex ? { ...entry, durationMinutes: minutes } : entry,
                          );
                          updateModule(moduleIndex, { ...module, lessons });
                        }}
                      />
                      <div className="flex flex-wrap items-center gap-2 text-xs">
                        <input
                          type="file"
                          accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.zip,.rar,image/*"
                          onChange={async (event) => {
                            const file = event.target.files?.[0];
                            if (!file) return;
                            if (isVideoFile(file)) {
                              setMessage('Vídeos não podem ser enviados por arquivo. Hospede o vídeo em outra plataforma e anexe somente o link.');
                              event.target.value = '';
                              return;
                            }
                            await handleUploadMaterial(moduleIndex, lessonIndex, file);
                            event.target.value = '';
                          }}
                        />
                        <button
                          type="button"
                          className="rounded border border-red-200 bg-red-50 px-2 py-1 font-semibold text-red-700"
                          onClick={() => handleAddVideoLinkMaterial(moduleIndex, lessonIndex)}
                        >
                          + Link de vídeo
                        </button>
                        <button type="button" className="inline-flex items-center gap-1 rounded bg-amber-500 px-2 py-1 font-semibold text-white" onClick={() => {
                          const lessons = module.lessons.map((entry, idx) => {
                            if (idx !== lessonIndex) return entry;
                            const existingTasks = Array.isArray(entry.tasks) ? entry.tasks : [];
                            return {
                              ...entry,
                              tasks: [
                                ...existingTasks,
                                {
                                  title: `Tarefa ${existingTasks.length + 1}`,
                                  description: '',
                                  instructions: '',
                                  maxScore: 10,
                                  dueDays: 7,
                                  formQuestions: [],
                                },
                              ],
                            };
                          });
                          updateModule(moduleIndex, { ...module, lessons });
                        }}><FaPlus /> Tarefa</button>
                        <button type="button" className="inline-flex items-center gap-1 rounded bg-purple-600 px-2 py-1 font-semibold text-white" onClick={() => {
                          const lessons = module.lessons.map((entry, idx) => idx === lessonIndex ? { ...entry, quiz: { title: 'Avaliação', maxScore: 10, passingScore: 7, questions: [] } } : entry);
                          updateModule(moduleIndex, { ...module, lessons });
                        }}><FaPlus /> Avaliação</button>
                        <button type="button" className="rounded border px-2 py-1" onClick={() => {
                          const lessons = module.lessons.filter((_, idx) => idx !== lessonIndex);
                          updateModule(moduleIndex, { ...module, lessons: lessons.length ? lessons : [emptyLesson()] });
                        }}>Remover aula</button>
                      </div>
                      <div className="mt-2 space-y-2">
                        {(lesson.materials || []).map((material, materialIndex) => (
                          <div key={`lesson-material-editor-${moduleIndex}-${lessonIndex}-${materialIndex}`} className="rounded border border-slate-200 bg-white p-2">
                            <div className="mb-1 flex items-center justify-between gap-2">
                              <p className="text-[11px] font-semibold text-slate-700">Anexo {materialIndex + 1}</p>
                              <button
                                type="button"
                                className="rounded border px-2 py-1 text-[10px]"
                                onClick={() => {
                                  const lessons = module.lessons.map((entry, idx) => {
                                    if (idx !== lessonIndex) return entry;
                                    return {
                                      ...entry,
                                      materials: (entry.materials || []).filter((_, idxMaterial) => idxMaterial !== materialIndex),
                                    };
                                  });
                                  updateModule(moduleIndex, { ...module, lessons });
                                }}
                              >
                                Excluir anexo
                              </button>
                            </div>
                            <input
                              className="mb-1 w-full rounded border p-1 text-xs"
                              placeholder="Nome do anexo"
                              value={material.name || ''}
                              onChange={(event) => {
                                const lessons = module.lessons.map((entry, idx) => {
                                  if (idx !== lessonIndex) return entry;
                                  return {
                                    ...entry,
                                    materials: (entry.materials || []).map((entryMaterial, idxMaterial) =>
                                      idxMaterial === materialIndex ? { ...entryMaterial, name: event.target.value } : entryMaterial,
                                    ),
                                  };
                                });
                                updateModule(moduleIndex, { ...module, lessons });
                              }}
                            />
                            <input
                              className="mb-1 w-full rounded border p-1 text-xs"
                              placeholder="URL do anexo (arquivo ou YouTube)"
                              value={getMaterialSourceUrl(material)}
                              onChange={(event) => {
                                const nextUrl = event.target.value;
                                const lessons = module.lessons.map((entry, idx) => {
                                  if (idx !== lessonIndex) return entry;
                                  return {
                                    ...entry,
                                    materials: (entry.materials || []).map((entryMaterial, idxMaterial) => {
                                      if (idxMaterial !== materialIndex) return entryMaterial;
                                      const youtube = Boolean(getYouTubeVideoId(nextUrl));
                                      if (isYouTubeHost(nextUrl) && !youtube) {
                                        setMessage('URL do YouTube inválida. Use o link direto do vídeo para exibição correta no mobile.');
                                      }
                                      return {
                                        ...entryMaterial,
                                        url: nextUrl,
                                        downloadUrl: nextUrl,
                                        type: youtube ? 'video' : entryMaterial.type || 'arquivo',
                                        mimeType: youtube ? 'video/youtube' : entryMaterial.mimeType,
                                        addedAt: entryMaterial.addedAt || new Date().toISOString(),
                                      };
                                    }),
                                  };
                                });
                                updateModule(moduleIndex, { ...module, lessons });
                              }}
                            />
                            <p className="text-[10px] uppercase tracking-wide text-slate-500">
                              Tipo: {material.type || 'arquivo'} {isYoutubeMaterial(material) ? '• YouTube' : ''}
                            </p>
                          </div>
                        ))}
                      </div>
                      <div className="mt-2 space-y-2">
                        {(lesson.tasks || []).map((task, taskIndex) => (
                          <div key={`task-editor-${moduleIndex}-${lessonIndex}-${taskIndex}`} className="rounded border border-emerald-200 bg-emerald-50 p-2">
                            <div className="mb-1 flex items-center justify-between gap-2">
                              <p className="text-xs font-semibold text-emerald-900">Tarefa {taskIndex + 1}</p>
                              <button
                                type="button"
                                className="rounded border border-emerald-300 bg-white px-2 py-1 text-[11px]"
                                onClick={() => {
                                  const lessons = module.lessons.map((entry, idx) => {
                                    if (idx !== lessonIndex) return entry;
                                    return {
                                      ...entry,
                                      tasks: (entry.tasks || []).filter((_, entryTaskIndex) => entryTaskIndex !== taskIndex),
                                    };
                                  });
                                  updateModule(moduleIndex, { ...module, lessons });
                                }}
                              >
                                Remover tarefa
                              </button>
                            </div>
                            <input
                              className="mb-1 w-full rounded border p-2 text-xs"
                              placeholder="Título da tarefa"
                              value={task.title || ''}
                              onChange={(event) => {
                                const lessons = module.lessons.map((entry, idx) => {
                                  if (idx !== lessonIndex) return entry;
                                  return {
                                    ...entry,
                                    tasks: (entry.tasks || []).map((entryTask, entryTaskIndex) =>
                                      entryTaskIndex === taskIndex ? { ...entryTask, title: event.target.value } : entryTask,
                                    ),
                                  };
                                });
                                updateModule(moduleIndex, { ...module, lessons });
                              }}
                            />
                            <textarea
                              className="w-full rounded border p-2 text-xs"
                              placeholder="Instruções da tarefa"
                              value={task.instructions || ''}
                              onChange={(event) => {
                                const lessons = module.lessons.map((entry, idx) => {
                                  if (idx !== lessonIndex) return entry;
                                  return {
                                    ...entry,
                                    tasks: (entry.tasks || []).map((entryTask, entryTaskIndex) =>
                                      entryTaskIndex === taskIndex ? { ...entryTask, instructions: event.target.value } : entryTask,
                                    ),
                                  };
                                });
                                updateModule(moduleIndex, { ...module, lessons });
                              }}
                            />
                            <input
                              className="mt-1 w-full rounded border p-2 text-xs"
                              type="number"
                              min="0"
                              step="0.5"
                              placeholder="Nota máxima da tarefa"
                              value={Number(task.maxScore ?? 10)}
                              onChange={(event) => {
                                const nextMaxScore = Math.max(0, Number(event.target.value || 0));
                                const lessons = module.lessons.map((entry, idx) => {
                                  if (idx !== lessonIndex) return entry;
                                  return {
                                    ...entry,
                                    tasks: (entry.tasks || []).map((entryTask, entryTaskIndex) =>
                                      entryTaskIndex === taskIndex ? { ...entryTask, maxScore: nextMaxScore } : entryTask,
                                    ),
                                  };
                                });
                                updateModule(moduleIndex, { ...module, lessons });
                              }}
                            />
                            <div className="mt-2 space-y-2">
                              {(task.formQuestions || []).map((question, questionIndex) => (
                                <div key={`task-question-${moduleIndex}-${lessonIndex}-${taskIndex}-${questionIndex}`} className="rounded border border-emerald-300 bg-white p-2">
                                  <div className="mb-1 flex items-center justify-between">
                                    <p className="text-[11px] font-semibold text-emerald-900">Pergunta {questionIndex + 1}</p>
                                    <button
                                      type="button"
                                      className="rounded border px-2 py-1 text-[10px]"
                                      onClick={() => {
                                        const lessons = module.lessons.map((entry, idx) => {
                                          if (idx !== lessonIndex) return entry;
                                          return {
                                            ...entry,
                                            tasks: (entry.tasks || []).map((entryTask, entryTaskIndex) =>
                                              entryTaskIndex === taskIndex
                                                ? {
                                                    ...entryTask,
                                                    formQuestions: (entryTask.formQuestions || []).filter((_, i) => i !== questionIndex),
                                                  }
                                                : entryTask,
                                            ),
                                          };
                                        });
                                        updateModule(moduleIndex, { ...module, lessons });
                                      }}
                                    >
                                      Remover
                                    </button>
                                  </div>
                                  <input
                                    className="mb-1 w-full rounded border p-1 text-xs"
                                    placeholder="Texto da pergunta"
                                    value={question.prompt || ''}
                                    onChange={(event) => {
                                      const lessons = module.lessons.map((entry, idx) => {
                                        if (idx !== lessonIndex) return entry;
                                        return {
                                          ...entry,
                                          tasks: (entry.tasks || []).map((entryTask, entryTaskIndex) =>
                                            entryTaskIndex === taskIndex
                                              ? {
                                                  ...entryTask,
                                                  formQuestions: (entryTask.formQuestions || []).map((entryQuestion, i) =>
                                                    i === questionIndex ? { ...entryQuestion, prompt: event.target.value } : entryQuestion,
                                                  ),
                                                }
                                              : entryTask,
                                          ),
                                        };
                                      });
                                      updateModule(moduleIndex, { ...module, lessons });
                                    }}
                                  />
                                  <select
                                    className="mb-1 w-full rounded border p-1 text-xs"
                                    value={question.type || 'text'}
                                    onChange={(event) => {
                                      const nextType = event.target.value;
                                      const lessons = module.lessons.map((entry, idx) => {
                                        if (idx !== lessonIndex) return entry;
                                        return {
                                          ...entry,
                                          tasks: (entry.tasks || []).map((entryTask, entryTaskIndex) =>
                                            entryTaskIndex === taskIndex
                                              ? {
                                                  ...entryTask,
                                                  formQuestions: (entryTask.formQuestions || []).map((entryQuestion, i) =>
                                                    i === questionIndex ? { ...entryQuestion, ...emptyQuestion(nextType), prompt: entryQuestion.prompt || '' } : entryQuestion,
                                                  ),
                                                }
                                              : entryTask,
                                          ),
                                        };
                                      });
                                      updateModule(moduleIndex, { ...module, lessons });
                                    }}
                                  >
                                    <option value="text">Texto</option>
                                    <option value="multiple_choice">Múltipla escolha</option>
                                    <option value="attachment">Anexo</option>
                                  </select>
                                  <input
                                    className="mb-1 w-full rounded border p-1 text-xs"
                                    type="number"
                                    min="0"
                                    step="0.5"
                                    placeholder="Pontuação da pergunta"
                                    value={Number(question.score ?? 1)}
                                    onChange={(event) => {
                                      const nextScore = Math.max(0, Number(event.target.value || 0));
                                      const lessons = module.lessons.map((entry, idx) => {
                                        if (idx !== lessonIndex) return entry;
                                        return {
                                          ...entry,
                                          tasks: (entry.tasks || []).map((entryTask, entryTaskIndex) =>
                                            entryTaskIndex === taskIndex
                                              ? {
                                                  ...entryTask,
                                                  formQuestions: (entryTask.formQuestions || []).map((entryQuestion, i) =>
                                                    i === questionIndex ? { ...entryQuestion, score: nextScore } : entryQuestion,
                                                  ),
                                                }
                                              : entryTask,
                                          ),
                                        };
                                      });
                                      updateModule(moduleIndex, { ...module, lessons });
                                    }}
                                  />
                                  {question.type === 'multiple_choice' ? (
                                    <div className="space-y-1">
                                      {(question.options || []).map((option, optionIndex) => (
                                        <div key={`task-option-editor-${optionIndex}`} className="flex items-center gap-1">
                                          <input
                                            className="w-full rounded border p-1 text-xs"
                                            placeholder={`Opção ${optionIndex + 1}`}
                                            value={option}
                                            onChange={(event) => {
                                              const lessons = module.lessons.map((entry, idx) => {
                                                if (idx !== lessonIndex) return entry;
                                                return {
                                                  ...entry,
                                                  tasks: (entry.tasks || []).map((entryTask, entryTaskIndex) =>
                                                    entryTaskIndex === taskIndex
                                                      ? {
                                                          ...entryTask,
                                                          formQuestions: (entryTask.formQuestions || []).map((entryQuestion, i) => {
                                                            if (i !== questionIndex) return entryQuestion;
                                                            const options = [...(entryQuestion.options || [])];
                                                            options[optionIndex] = event.target.value;
                                                            return { ...entryQuestion, options };
                                                          }),
                                                        }
                                                      : entryTask,
                                                  ),
                                                };
                                              });
                                              updateModule(moduleIndex, { ...module, lessons });
                                            }}
                                          />
                                          <button
                                            type="button"
                                            className="rounded border px-2 py-1 text-[10px]"
                                            onClick={() => {
                                              const lessons = module.lessons.map((entry, idx) => {
                                                if (idx !== lessonIndex) return entry;
                                                return {
                                                  ...entry,
                                                  tasks: (entry.tasks || []).map((entryTask, entryTaskIndex) =>
                                                    entryTaskIndex === taskIndex
                                                      ? {
                                                          ...entryTask,
                                                          formQuestions: (entryTask.formQuestions || []).map((entryQuestion, i) =>
                                                            i === questionIndex
                                                              ? { ...entryQuestion, options: (entryQuestion.options || []).filter((_, oi) => oi !== optionIndex) }
                                                              : entryQuestion,
                                                          ),
                                                        }
                                                      : entryTask,
                                                  ),
                                                };
                                              });
                                              updateModule(moduleIndex, { ...module, lessons });
                                            }}
                                          >
                                            x
                                          </button>
                                        </div>
                                      ))}
                                      <button
                                        type="button"
                                        className="rounded border px-2 py-1 text-[11px]"
                                        onClick={() => {
                                          const lessons = module.lessons.map((entry, idx) => {
                                            if (idx !== lessonIndex) return entry;
                                            return {
                                              ...entry,
                                              tasks: (entry.tasks || []).map((entryTask, entryTaskIndex) =>
                                                entryTaskIndex === taskIndex
                                                  ? {
                                                      ...entryTask,
                                                      formQuestions: (entryTask.formQuestions || []).map((entryQuestion, i) =>
                                                        i === questionIndex
                                                          ? { ...entryQuestion, options: [...(entryQuestion.options || []), ''] }
                                                          : entryQuestion,
                                                      ),
                                                    }
                                                  : entryTask,
                                              ),
                                            };
                                          });
                                          updateModule(moduleIndex, { ...module, lessons });
                                        }}
                                      >
                                        + Opção
                                      </button>
                                    </div>
                                  ) : null}
                                </div>
                              ))}
                              <button
                                type="button"
                                className="rounded border border-emerald-300 bg-white px-2 py-1 text-[11px]"
                                onClick={() => {
                                  const lessons = module.lessons.map((entry, idx) => {
                                    if (idx !== lessonIndex) return entry;
                                    return {
                                      ...entry,
                                      tasks: (entry.tasks || []).map((entryTask, entryTaskIndex) =>
                                        entryTaskIndex === taskIndex
                                          ? { ...entryTask, formQuestions: [...(entryTask.formQuestions || []), emptyQuestion()] }
                                          : entryTask,
                                      ),
                                    };
                                  });
                                  updateModule(moduleIndex, { ...module, lessons });
                                }}
                              >
                                + Pergunta da tarefa
                              </button>
                            </div>
                          </div>
                        ))}

                        {lesson.quiz ? (
                          <div className="rounded border border-purple-200 bg-purple-50 p-2">
                            <div className="mb-1 flex items-center justify-between gap-2">
                              <p className="text-xs font-semibold text-purple-900">Avaliação configurada</p>
                              <button
                                type="button"
                                className="rounded border border-purple-300 bg-white px-2 py-1 text-[11px]"
                                onClick={() => {
                                  const lessons = module.lessons.map((entry, idx) =>
                                    idx === lessonIndex ? { ...entry, quiz: null } : entry,
                                  );
                                  updateModule(moduleIndex, { ...module, lessons });
                                }}
                              >
                                Remover avaliação
                              </button>
                            </div>
                            <input
                              className="mb-1 w-full rounded border p-2 text-xs"
                              placeholder="Título da avaliação"
                              value={lesson.quiz.title || ''}
                              onChange={(event) => {
                                const lessons = module.lessons.map((entry, idx) =>
                                  idx === lessonIndex
                                    ? { ...entry, quiz: { ...(entry.quiz || {}), title: event.target.value } }
                                    : entry,
                                );
                                updateModule(moduleIndex, { ...module, lessons });
                              }}
                            />
                            <input
                              className="mb-1 w-full rounded border p-2 text-xs"
                              type="number"
                              min="0"
                              step="0.5"
                              placeholder="Nota máxima da avaliação"
                              value={Number(lesson.quiz.maxScore ?? 10)}
                              onChange={(event) => {
                                const nextMaxScore = Math.max(0, Number(event.target.value || 0));
                                const lessons = module.lessons.map((entry, idx) =>
                                  idx === lessonIndex && entry.quiz
                                    ? { ...entry, quiz: { ...entry.quiz, maxScore: nextMaxScore } }
                                    : entry,
                                );
                                updateModule(moduleIndex, { ...module, lessons });
                              }}
                            />
                            <div className="space-y-2">
                              {(lesson.quiz.questions || []).map((question, questionIndex) => (
                                <div key={`quiz-question-${moduleIndex}-${lessonIndex}-${questionIndex}`} className="rounded border border-purple-300 bg-white p-2">
                                  <div className="mb-1 flex items-center justify-between">
                                    <p className="text-[11px] font-semibold text-purple-900">Pergunta {questionIndex + 1}</p>
                                    <button
                                      type="button"
                                      className="rounded border px-2 py-1 text-[10px]"
                                      onClick={() => {
                                        const lessons = module.lessons.map((entry, idx) => {
                                          if (idx !== lessonIndex || !entry.quiz) return entry;
                                          return {
                                            ...entry,
                                            quiz: {
                                              ...entry.quiz,
                                              questions: (entry.quiz.questions || []).filter((_, i) => i !== questionIndex),
                                            },
                                          };
                                        });
                                        updateModule(moduleIndex, { ...module, lessons });
                                      }}
                                    >
                                      Remover
                                    </button>
                                  </div>
                                  <input
                                    className="mb-1 w-full rounded border p-1 text-xs"
                                    placeholder="Texto da pergunta"
                                    value={question.prompt || ''}
                                    onChange={(event) => {
                                      const lessons = module.lessons.map((entry, idx) => {
                                        if (idx !== lessonIndex || !entry.quiz) return entry;
                                        return {
                                          ...entry,
                                          quiz: {
                                            ...entry.quiz,
                                            questions: (entry.quiz.questions || []).map((entryQuestion, i) =>
                                              i === questionIndex ? { ...entryQuestion, prompt: event.target.value } : entryQuestion,
                                            ),
                                          },
                                        };
                                      });
                                      updateModule(moduleIndex, { ...module, lessons });
                                    }}
                                  />
                                  <select
                                    className="w-full rounded border p-1 text-xs"
                                    value={question.type || 'text'}
                                    onChange={(event) => {
                                      const nextType = event.target.value;
                                      const lessons = module.lessons.map((entry, idx) => {
                                        if (idx !== lessonIndex || !entry.quiz) return entry;
                                        return {
                                          ...entry,
                                          quiz: {
                                            ...entry.quiz,
                                            questions: (entry.quiz.questions || []).map((entryQuestion, i) =>
                                              i === questionIndex ? { ...entryQuestion, ...emptyQuestion(nextType), prompt: entryQuestion.prompt || '' } : entryQuestion,
                                            ),
                                          },
                                        };
                                      });
                                      updateModule(moduleIndex, { ...module, lessons });
                                    }}
                                  >
                                    <option value="text">Texto</option>
                                    <option value="multiple_choice">Múltipla escolha</option>
                                    <option value="attachment">Anexo</option>
                                  </select>
                                  <input
                                    className="mt-1 w-full rounded border p-1 text-xs"
                                    type="number"
                                    min="0"
                                    step="0.5"
                                    placeholder="Pontuação da pergunta"
                                    value={Number(question.score ?? 1)}
                                    onChange={(event) => {
                                      const nextScore = Math.max(0, Number(event.target.value || 0));
                                      const lessons = module.lessons.map((entry, idx) => {
                                        if (idx !== lessonIndex || !entry.quiz) return entry;
                                        return {
                                          ...entry,
                                          quiz: {
                                            ...entry.quiz,
                                            questions: (entry.quiz.questions || []).map((entryQuestion, i) =>
                                              i === questionIndex ? { ...entryQuestion, score: nextScore } : entryQuestion,
                                            ),
                                          },
                                        };
                                      });
                                      updateModule(moduleIndex, { ...module, lessons });
                                    }}
                                  />
                                  {question.type === 'multiple_choice' ? (
                                    <div className="mt-1 space-y-1">
                                      {(question.options || []).map((option, optionIndex) => (
                                        <div key={`quiz-option-editor-${optionIndex}`} className="flex items-center gap-1">
                                          <input
                                            className="w-full rounded border p-1 text-xs"
                                            placeholder={`Opção ${optionIndex + 1}`}
                                            value={option}
                                            onChange={(event) => {
                                              const lessons = module.lessons.map((entry, idx) => {
                                                if (idx !== lessonIndex || !entry.quiz) return entry;
                                                return {
                                                  ...entry,
                                                  quiz: {
                                                    ...entry.quiz,
                                                    questions: (entry.quiz.questions || []).map((entryQuestion, i) => {
                                                      if (i !== questionIndex) return entryQuestion;
                                                      const options = [...(entryQuestion.options || [])];
                                                      options[optionIndex] = event.target.value;
                                                      return { ...entryQuestion, options };
                                                    }),
                                                  },
                                                };
                                              });
                                              updateModule(moduleIndex, { ...module, lessons });
                                            }}
                                          />
                                          <button
                                            type="button"
                                            className="rounded border px-2 py-1 text-[10px]"
                                            onClick={() => {
                                              const lessons = module.lessons.map((entry, idx) => {
                                                if (idx !== lessonIndex || !entry.quiz) return entry;
                                                return {
                                                  ...entry,
                                                  quiz: {
                                                    ...entry.quiz,
                                                    questions: (entry.quiz.questions || []).map((entryQuestion, i) =>
                                                      i === questionIndex
                                                        ? { ...entryQuestion, options: (entryQuestion.options || []).filter((_, oi) => oi !== optionIndex) }
                                                        : entryQuestion,
                                                    ),
                                                  },
                                                };
                                              });
                                              updateModule(moduleIndex, { ...module, lessons });
                                            }}
                                          >
                                            x
                                          </button>
                                        </div>
                                      ))}
                                      <button
                                        type="button"
                                        className="rounded border px-2 py-1 text-[11px]"
                                        onClick={() => {
                                          const lessons = module.lessons.map((entry, idx) => {
                                            if (idx !== lessonIndex || !entry.quiz) return entry;
                                            return {
                                              ...entry,
                                              quiz: {
                                                ...entry.quiz,
                                                questions: (entry.quiz.questions || []).map((entryQuestion, i) =>
                                                  i === questionIndex
                                                    ? { ...entryQuestion, options: [...(entryQuestion.options || []), ''] }
                                                    : entryQuestion,
                                                ),
                                              },
                                            };
                                          });
                                          updateModule(moduleIndex, { ...module, lessons });
                                        }}
                                      >
                                        + Opção
                                      </button>
                                    </div>
                                  ) : null}
                                </div>
                              ))}
                              <button
                                type="button"
                                className="rounded border border-purple-300 bg-white px-2 py-1 text-[11px]"
                                onClick={() => {
                                  const lessons = module.lessons.map((entry, idx) => {
                                    if (idx !== lessonIndex || !entry.quiz) return entry;
                                    return {
                                      ...entry,
                                      quiz: {
                                        ...entry.quiz,
                                        questions: [...(entry.quiz.questions || []), emptyQuestion()],
                                      },
                                    };
                                  });
                                  updateModule(moduleIndex, { ...module, lessons });
                                }}
                              >
                                + Pergunta da avaliação
                              </button>
                            </div>
                          </div>
                        ) : null}
                      </div>
                    </div>
                  ))}
                  <button type="button" className="inline-flex items-center gap-1 rounded bg-blue-600 px-2 py-1 text-xs font-semibold text-white" onClick={() => updateModule(moduleIndex, { ...module, lessons: [...module.lessons, emptyLesson()] })}><FaPlus /> Aula</button>
                </div>
              ))}
              <button type="button" className="mt-2 inline-flex items-center gap-1 rounded border px-3 py-1 text-xs" onClick={() => setForm((prev) => ({ ...prev, modules: [...prev.modules, emptyModule()] }))}>
                <FaPlus /> Adicionar módulo
              </button>
            </div>

            <div className="rounded-xl border border-slate-200 p-3">
              <h3 className="mb-2 text-sm font-semibold text-slate-700">Meus cursos</h3>
              <div className="space-y-2">
                {creatorCourses.length === 0 ? <p className="text-xs text-slate-500">Nenhum curso para editar.</p> : null}
                {creatorCourses.map((course) => (
                  <button
                    key={course.id}
                    type="button"
                    onClick={() => startEditCourse(course)}
                    className={`w-full rounded-lg border p-2 text-left text-xs ${editingCourseId === course.id ? 'border-blue-500 bg-blue-50' : 'border-slate-200'}`}
                  >
                    <p className="font-semibold text-slate-700">{course.title}</p>
                    <p className="text-slate-500">{course.status} • {course.modules?.length || 0} módulos</p>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </section>
      ) : null}

      {activeTab === TABS.COORDINATOR && canCoordinate ? (
        <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
          <h2 className="mb-4 flex items-center gap-2 text-lg font-semibold text-slate-800">
            <FaUsersCog className="text-blue-600" /> Coordenação de Turmas e Acessos
            <FaInfoCircle className="text-slate-400" title="Gerencie turmas, concessões e matrículas com controle central." />
          </h2>
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <div className="rounded-xl border border-slate-200 p-3">
              <h3 className="mb-2 text-sm font-semibold">{editingClassroomId ? 'Editar turma' : 'Criar turma'}</h3>
              <input className="mb-2 w-full rounded border p-2" placeholder="Nome da turma" value={classroomForm.name} onChange={(event) => setClassroomForm((prev) => ({ ...prev, name: event.target.value }))} />
              <textarea className="mb-2 w-full rounded border p-2" placeholder="Descrição" value={classroomForm.description} onChange={(event) => setClassroomForm((prev) => ({ ...prev, description: event.target.value }))} />
              <div className="mb-2">
                <p className="mb-1 text-xs font-semibold">Status</p>
                <div className="flex items-center gap-4 text-xs">
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="classroom-status"
                      checked={classroomForm.status === 'ACTIVE'}
                      onChange={() => setClassroomForm((prev) => ({ ...prev, status: 'ACTIVE' }))}
                    />
                    Ativo
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="classroom-status"
                      checked={classroomForm.status === 'INACTIVE'}
                      onChange={() => setClassroomForm((prev) => ({ ...prev, status: 'INACTIVE' }))}
                    />
                    Inativo
                  </label>
                </div>
              </div>
              <select className="mb-2 w-full rounded border p-2" value={classroomForm.coordinatorUserId || ''} onChange={(event) => setClassroomForm((prev) => ({ ...prev, coordinatorUserId: Number(event.target.value) }))}>
                <option value="">Selecione coordenador</option>
                {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
              </select>

              <p className="mb-1 text-xs font-semibold">Alunos da turma</p>
              <input
                className="mb-2 w-full rounded border p-2 text-xs"
                placeholder="Pesquisar aluno por nome ou e-mail"
                value={studentSearchTerm}
                onChange={(event) => setStudentSearchTerm(event.target.value)}
              />
              <div className="max-h-28 overflow-auto rounded border p-2 text-xs">
                {filteredStudentUsers.map((u) => (
                  <label key={`student-${u.id}`} className="mb-1 flex items-center gap-2">
                    <input type="checkbox" checked={classroomForm.studentIds.includes(u.id)} onChange={() => setClassroomForm((prev) => ({ ...prev, studentIds: toggleMultiSelect(prev.studentIds, u.id) }))} />
                    {u.name}
                  </label>
                ))}
                {filteredStudentUsers.length === 0 ? (
                  <p className="text-slate-500">Nenhum aluno encontrado para o filtro informado.</p>
                ) : null}
              </div>

              <p className="mb-1 mt-2 text-xs font-semibold">Cursos da turma</p>
              <div className="max-h-28 overflow-auto rounded border p-2 text-xs">
                {courses.map((course) => (
                  <label key={`course-access-${course.id}`} className="mb-1 flex items-center gap-2">
                    <input type="checkbox" checked={classroomForm.courseIds.includes(course.id)} onChange={() => setClassroomForm((prev) => ({ ...prev, courseIds: toggleMultiSelect(prev.courseIds, course.id) }))} />
                    {course.title}
                  </label>
                ))}
              </div>

              <div className="mt-2 flex items-center gap-2">
                <button type="button" className="rounded bg-indigo-600 px-3 py-1 text-xs text-white" onClick={createClassroom}>
                  {editingClassroomId ? 'Atualizar turma' : 'Salvar turma'}
                </button>
                {editingClassroomId ? (
                  <button type="button" className="rounded border border-slate-300 px-3 py-1 text-xs text-slate-700" onClick={cancelEditClassroom}>
                    Cancelar edição
                  </button>
                ) : null}
              </div>
            </div>

            <div className="rounded-xl border border-slate-200 p-3">
              <h3 className="mb-2 text-sm font-semibold">Liberar acesso direto (sem turma)</h3>
              <select className="mb-2 w-full rounded border p-2" value={grantForm.courseId || ''} onChange={(event) => setGrantForm((prev) => ({ ...prev, courseId: Number(event.target.value) || null }))}>
                <option value="">Selecione curso</option>
                {courses.map((course) => <option key={`grant-course-${course.id}`} value={course.id}>{course.title}</option>)}
              </select>
              <select className="mb-2 w-full rounded border p-2" value={grantForm.userId || ''} onChange={(event) => setGrantForm((prev) => ({ ...prev, userId: Number(event.target.value) || null }))}>
                <option value="">Selecione aluno</option>
                {filteredStudentUsers.map((u) => <option key={`grant-user-${u.id}`} value={u.id}>{u.name}</option>)}
              </select>
              <input className="mb-2 w-full rounded border p-2" type="datetime-local" value={grantForm.accessStartAt} onChange={(event) => setGrantForm((prev) => ({ ...prev, accessStartAt: event.target.value }))} />
              <input className="mb-2 w-full rounded border p-2" type="datetime-local" value={grantForm.accessEndAt} onChange={(event) => setGrantForm((prev) => ({ ...prev, accessEndAt: event.target.value }))} />
              <button type="button" className="rounded bg-orange-600 px-3 py-1 text-xs text-white" onClick={grantDirectAccess}>Conceder acesso</button>

              <h4 className="mb-1 mt-4 text-sm font-semibold">Turmas cadastradas</h4>
              <div className="max-h-40 overflow-auto space-y-2">
                {classrooms.map((classroom) => (
                  <div key={classroom.id} className="rounded border border-slate-200 p-2 text-xs">
                    <p className="font-semibold">{classroom.name}</p>
                    <p>{classroom.description || 'Sem descrição'}</p>
                    <p>Status: {classroom.status === 'INACTIVE' ? 'Inativo' : 'Ativo'}</p>
                    <p>Alunos: {classroom.studentIds.length} | Cursos: {classroom.courseIds.length}</p>
                    <div className="mt-2 flex items-center gap-2">
                      <button
                        type="button"
                        className="rounded border border-blue-200 bg-blue-50 px-2 py-1 text-[11px] font-semibold text-blue-700"
                        onClick={() => startEditClassroom(classroom)}
                      >
                        Editar
                      </button>
                      <button
                        type="button"
                        className="rounded border border-rose-200 bg-rose-50 px-2 py-1 text-[11px] font-semibold text-rose-700"
                        onClick={() => deleteClassroom(classroom)}
                      >
                        Excluir
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      ) : null}

      {materialUploadStatus.visible ? (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-900/60 p-4">
          <div className="w-full max-w-md rounded-xl bg-white p-4 shadow-2xl">
            <p className="text-sm font-semibold text-slate-800">Enviando material da aula...</p>
            <p className="mt-1 truncate text-xs text-slate-500">{materialUploadStatus.fileName}</p>
            <div className="mt-3 h-3 w-full overflow-hidden rounded-full bg-slate-200">
              <div
                className="h-full rounded-full bg-blue-600 transition-all duration-200"
                style={{ width: `${materialUploadStatus.progress}%` }}
              />
            </div>
            <p className="mt-2 text-right text-xs font-semibold text-slate-700">
              {materialUploadStatus.progress}%
            </p>
          </div>
        </div>
      ) : null}
    </div>
  );
}
